import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
} from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { unzipSync } from 'fflate';
import * as mammoth from 'mammoth';
import {
  parseCatalogueDataDescriptors,
  catalogueDescriptorSubtitle,
  hasCatalogueDescriptorFieldValue,
  type CatalogueDataDescriptorViewModel,
  ApiExplorerComponent,
  type ApiExplorerContext,
  splitFeedbackItems,
  type AdditionalInfoFaqRow,
} from '@angular-workspace/feature';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import {
  AssetHeaderComponent,
  AssetHeaderConfig,
} from '../../../shared/components/asset-header/asset-header.component';
import { SimilarDatasetsComponent } from '../../../shared/components/similar-datasets/similar-datasets.component';
import { FeatureCardData } from '../../../shared/components/feature-card/feature-card.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import {
  DateRangePickerComponent,
  DateRange,
} from '../../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import {
  CatalogueApiService,
  AclApdApiService,
  AuthApiService,
  SearchCriteria,
  RsApiService,
  UserInteractionApiService,
  FileApiService,
  DiscussionApiService,
  RolesApiService,
  Role,
} from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { FeatureCardMapperService } from '../../../shared/services/feature-card-mapper.service';
import { ToasterService } from '../../../services/toaster.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import {
  DiscussionItem,
  DiscussionAttachment,
} from '../../discussions/discussion-home/discussion-home.component';
import {
  CreateDiscussionOffcanvasComponent,
  DiscussionFormData,
} from '../../../shared/components/create-discussion-offcanvas/create-discussion-offcanvas.component';
import { AssetDiscussionListComponent } from '../../../shared/components/asset-discussion-list/asset-discussion-list.component';
import { SqlPlaygroundComponent } from '../sql-visualisation/sql-playground.component';
import { DatasetVisualisationComponent } from '../visualisation/dataset-visualisation.component';
import { ModelPopupComponent } from '../../../shared/components/popups/model-popup/model-popup.component';
import { GatewayDataComponent } from '../../../shared/components/popups/gateway-data/gateway-data.component';
import { OgcStacDataComponent } from '../../../shared/components/popups/ogc-stac-data/ogc-stac-data.component';
import { OgcFeatureDataComponent } from '../../../shared/components/popups/ogc-feature-data/ogc-feature-data.component';
import { RequestAccessComponent } from '../../../shared/components/popups/request-access/request-access.component';
import { normalizeAclAccessStatus } from '../../normalize-acl-access-status';
import { parseItemAuditEnabled } from '../../../shared/utils/catalogue-asset-detail-navigation.util';
import { ratingStarFraction } from '../../../shared/utils/user-feedback.util';
import { environment } from '../../../../environments/environment';

interface ResourceServer {
  readonly name: string;
  readonly url?: string;
  readonly accessTypes?: readonly string[];
  readonly queryTypes?: readonly string[];
  readonly searchValues?: readonly string[];
}

interface DatasetReviewItem {
  id: string;
  userId: string;
  email: string;
  name: string;
  organisation: string;
  initials: string;
  comment: string;
  ratingValue: number;
  createdAt: string;
  createdAtMs: number;
  status: 'pending' | 'approved' | 'rejected';
}

type DatasetDetailTab =
  | 'dataset'
  | 'details'
  | 'visualisation'
  | 'sql-playground'
  | 'discussion'
  | 'additional-info';

const DATASET_DETAIL_TABS: DatasetDetailTab[] = [
  'dataset',
  'details',
  'visualisation',
  'sql-playground',
  'discussion',
  'additional-info',
];

@Component({
  selector: 'app-dataset-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    SimilarDatasetsComponent,
    ModalComponent,
    LoginModalComponent,
    CreateDiscussionOffcanvasComponent,
    AssetDiscussionListComponent,
    SqlPlaygroundComponent,
    DatasetVisualisationComponent,
    DateRangePickerComponent,
    PdfViewerModule,
    ModelPopupComponent,
    GatewayDataComponent,
    OgcStacDataComponent,
    OgcFeatureDataComponent,
    RequestAccessComponent,
    ApiExplorerComponent,
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class DatasetDetailComponent implements OnInit, OnDestroy {
  private catalogueApiService = inject(CatalogueApiService);
  private aclApdApiService = inject(AclApdApiService);
  private authApiService = inject(AuthApiService);
  private rolesApiService = inject(RolesApiService);
  private rsApiService = inject(RsApiService);
  private fileApiService = inject(FileApiService);
  private helperService = inject(HelperService);
  private formatService = inject(FormatService);
  private featureCardMapper = inject(FeatureCardMapperService);
  private toasterService = inject(ToasterService);
  private workspaceContext = inject(WorkspaceContextService);
  private keycloak = inject(KeycloakService);
  private keycloakSubscription?: Subscription;
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  private discussionApiService = inject(DiscussionApiService);
  private sanitizer = inject(DomSanitizer);

  datasetId: string | null = null;
  /** When true, initial item GET records a catalogue view audit (from catalogue navigation only). */
  private itemAuditEnabled = false;
  showApiExplorer = false;
  apiExplorerContext: ApiExplorerContext | null = null;
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Datasets', route: '/datasets' },
      { label: 'Dataset Details' },
    ],
    title: 'Dataset Details',
    description: 'Loading dataset information...',
  };
  similarDatasets: FeatureCardData[] = [];
  datasetDescription: string = '';
  datasetTags: string[] = [];
  /** Concept labels from item API (concepts / concept). */
  datasetConcepts: string[] = [];
  showTagsModal: boolean = false;
  readonly MAX_VISIBLE_TAGS = 8; // Approximate number of tags that fit in 2 rows
  additionalInformation: string = '';
  feedbackFaqRows: AdditionalInfoFaqRow[] = [];
  feedbackInfoText = '';
  expandedFaqIndex: number | null = 0;

  // Activity Overview metrics
  activityMetrics = {
    likes: 0,
    downloads: 0,
    views: 0,
    bookmarks: 0,
    rating: null as number | null,
  };

  // Tab state
  activeTab: DatasetDetailTab = 'dataset';
  isLoggedIn: boolean = false;
  pendingTab: DatasetDetailTab | null = null;
  showSampleDataModal: boolean = false;
  /** Sample data from item API dataSample; null when API does not return dataSample. */
  sampleData: any = null;
  showDataDescriptorsModal: boolean = false;
  selectedDataElement: string = '';
  showViewCodeModal: boolean = false;
  viewCodeActiveTab: string = '';
  viewCodeTabs: Array<{ name: string; resourceServer: any }> = [];
  viewCodeByTab: { [key: string]: string } = {};
  showQueryDataModal: boolean = false;
  showRequestAccessModal: boolean = false;
  /** True when API license is prefixed with __ (departmental / internal dataset marker). */
  licenseUsesOpenMarker = false;
  readonly departmentalDatasetNote =
    'This is a departmental dataset, used internally.';
  showLoginModal: boolean = false;
  showGiveRatingModal: boolean = false;
  selectedRating: number = 0;
  hoveredRating: number = 0;
  ratingFeedback = '';
  ratingFormSubmitted = false;
  isSubmittingRating = false;
  isViewingOwnRating = false;
  isEditingOwnRating = false;
  private editBaselineRating = 0;
  private editBaselineFeedback = '';
  myDatasetReview: DatasetReviewItem | null = null;
  /** This user's feedback for the dataset, including a still-pending submission. */
  ownSubmittedReview: DatasetReviewItem | null = null;
  userFeedbackItems: DatasetReviewItem[] = [];
  userFeedbackSummary: { averageRating: number | null; totalRatings: number } | null = null;
  userRatingsSort: 'new' | 'old' = 'new';
  userRatingsFilter: number | null = null;
  userRatingsPage = 0;
  private userFeedbackRequest = 0;
  showUserRatingsFilterMenu = false;
  readonly userRatingsPageSize = 3;
  readonly ratingFeedbackMax = 200;
  showGetDataModal: boolean = false;

  // Get Data Modal state
  getDataLoading: boolean = false;
  getDataResponse: any = null;
  getDataCurrentPage: number = 1;
  getDataPageSize: number = 8;
  getDataTotalPages: number = 1;
  getDataTotalEntries: number = 0;
  getDataEntries: any[] = [];
  /** Maximum pages viewable in the Get Data table before prompting CSV download. */
  readonly getDataMaxViewPages = 200;
  /** Total records above which the Get Data table shows the view-limit alert. */
  readonly getDataMaxViewRecords = 2000;
  /** 'entity' = NGSI-LD table; 'file' = file from file server shown in PDF viewer */
  getDataMode: 'entity' | 'file' = 'entity';
  /** For FILE mode: URL to show in PDF viewer (presigned or blob). */
  getDataPdfUrl: string | null = null;
  /** Legacy: HTML from mammoth when PDF preview is not used (kept for edge cases). */
  getDataDocxHtml: string = '';
  /** For FILE mode non-PDF: download URL and filename. */
  getDataFileDownloadUrl: string | null = null;
  getDataFileName: string = '';
  /** Multiple files from the files API or paths inside a ZIP â€” left-rail picker in Get Data (file mode). */
  getDataFileKeys: string[] = [];
  getDataSelectedFileKey: string | null = null;
  /** True while loading a different file after the modal is already open. */
  getDataFileSwitchLoading: boolean = false;
  /** Unpacked ZIP entries when the databank download is an archive with several previewable files. */
  private getDataZipContents: Record<string, Uint8Array> | null = null;
  /** True when files API / version list has no files â€” show message instead of preview. */
  getDataNoFilesAvailable = false;

  /** Get Data - Gateway/OGC popup state (server-specific modals) */
  isFeatureDataOpen = false;
  featureDataMode: {
    type: 'GATEWAY' | 'OGC_STAC' | 'OGC_FEATURE' | 'DEFAULT';
    dataModel?: 'model1' | 'model2';
    accessTypes?: readonly string[];
  } = {
    type: 'GATEWAY',
    dataModel: 'model1',
    accessTypes: [],
  };

  queryDataForm: {
    requestAs: 'Individual' | 'Organisation';
    organisationName: string;
    purposeDescription: string;
    agreeToTerms: boolean;
  } = {
    requestAs: 'Organisation',
    organisationName: '',
    purposeDescription: '',
    agreeToTerms: false,
  };
  isOrganisationNameDisabled: boolean = false;
  /** True when user has joined/created an organisation (from getUserInfo). When true, modal shows only Organisation Name (disabled) + form; when false, shows Individual/Organisation choice. */
  userHasOrganisation: boolean = false;
  readonly PURPOSE_MAX_LENGTH = 200;

  /** Query Data modal: date range filter (single picker, range mode like asset filters) */
  queryDataDateRange: DateRange = { startDate: null, endDate: null };
  /** True while temporal entities API is in progress (shows loader in modal). */
  queryDataLoading: boolean = false;
  /** JSON response from temporal entities API â€“ shown below dates, used for Download JSON. */
  queryDataResponse: any = null;
  /** Used to cancel the temporal entities API when user closes the Query Data modal. */
  queryDataAbortController: AbortController | null = null;

  /** View File modal: open file (PDF/DOCX) in a modal with optional tabs */
  showViewFileModal: boolean = false;
  viewFileActiveTab: 'pdf' | 'docx' = 'pdf';
  viewFilePdfUrl: string | null = null;
  viewFileDocxHtml: string = '';
  viewFileDocxDownloadUrl: string | null = null;
  viewFileDocxFileName: string = '';
  viewFileLoading: boolean = false;

  // Discussion data
  datasetDiscussions: DiscussionItem[] = [];
  datasetDiscussionsLoading: boolean = false;
  datasetDiscussionsError: string | null = null;

  // Create Discussion Offcanvas
  showCreateDiscussionOffcanvas: boolean = false;

  // Last-resort fallback only; the real sample scripts live in the
  // viewCode-*.py files under public/ and are loaded via loadCodeFileForResourceServer().
  viewCode: string =
    '# Unable to load sample code. Please refresh the page and try again.';
  /** Data descriptors from item API `dataDescriptor` for the catalogue modal. */
  dataDescriptors: CatalogueDataDescriptorViewModel[] = [];
  datasetDetails: {
    uploadedBy?: string;
    organisationType?: string;
    industry?: string;
    organizationName?: string;
    yearRange?: string;
    geoCoverage?: string;
    datasetPermission?: string;
    lastUpdated?: string;
    fileFormat?: string;
    license?: string;
    uploadFrequency?: string;
    dataReadiness?: number;
    verifiedBy?: string;
    resourceType?: string;
    accessPolicy?: string;
    [key: string]: any;
  } = {};

  datasetCardDetails: {
    badge?: string;
    createdAt?: string;
    updatedAt?: string;
    fileFormat?: string;
    uploadFrequency?: string;
    verifiedBy?: string;
    dataReadiness?: string;
  } = {};

  // PDF Viewer modal state
  showDataReadinessPdfModal: boolean = false;
  dataReadinessPdfUrl: string | null = null;

  accessStatus: {
    type?: string;
    [key: string]: any;
  } = {};
  /** True after a successful access request submit until has_access returns success. */
  private accessRequestPending = false;
  private static readonly ACCESS_PENDING_URN =
    'urn:dx:apdServerPanel:access-pending';
  private static readonly ACCESS_SUCCESS_URN = 'urn:dx:apdServerPanel:success';
  private static readonly ACCESS_NO_ACCESS_URN =
    'urn:dx:apdServerPanel:no-access';
  private static readonly RATING_EDITOR_INTENT_KEY = 'cdpg.openRatingEditor';
  /** Guards `checkUserAccess` so older responses cannot overwrite newer state. */
  private accessCheckSeq = 0;
  currentUserSub: string | null = null;
  currentUserEmail: string | null = null;
  private hasLoadedCurrentUserInfo = false;

  // Store full dataset data to access resourceServer
  fullDatasetData: any = null;

  // Version Files data for Details tab
  versionFiles: Array<{
    name: string;
    size?: string;
    isSelected?: boolean;
  }> = [];
  versionFilesTotalSize: string = '';
  versionFilesLastUpdated: string = '';
  /** Preview API responses keyed by file name (populated after files API 200 OK). */
  versionFilePreviews: Record<string, { data: any; status: number }> = {};
  /** Parsed table for Details tab Data Bank Viewer (from selected file's preview). */
  detailsPreviewHeaders: string[] = [];
  detailsPreviewEntries: any[] = [];

  // Details tab data loading state
  detailsTabDataLoading: boolean = false;
  /** True while loading preview for the selected file (on file click). */
  detailsPreviewLoading: boolean = false;
  /** File metadata from getFileMetadata API (displayed below Version Files). */
  fileMetadata: any = null;
  /** True while loading file metadata. */
  fileMetadataLoading: boolean = false;

  // Bookmark and Like state
  isBookmarked: boolean = false;
  isLiked: boolean = false;

  private route = inject(ActivatedRoute);
  private userInteractionApiService = inject(UserInteractionApiService);
  private googleAnalytics = inject(GoogleAnalyticsService);

  get visibleTags(): string[] {
    return this.datasetTags.slice(0, this.MAX_VISIBLE_TAGS);
  }

  get remainingTagsCount(): number {
    return Math.max(0, this.datasetTags.length - this.MAX_VISIBLE_TAGS);
  }

  get allTags(): string[] {
    return this.datasetTags;
  }

  get hasDatasetDetails(): boolean {
    return this.datasetDetails && Object.keys(this.datasetDetails).length > 0;
  }

  /** Comma-separated file keys (from files API) to show to the left of "Version Files" heading. */
  get versionFileKeys(): string {
    if (!this.versionFiles?.length) return '';
    return this.versionFiles.map((f) => f.name).join(', ');
  }

  /** Version files that are PDF (by extension). */
  get versionFilesPdf(): Array<{
    name: string;
    size?: string;
    isSelected?: boolean;
  }> {
    if (!this.versionFiles?.length) return [];
    return this.versionFiles.filter((f) =>
      (f.name || '').toLowerCase().endsWith('.pdf'),
    );
  }

  /** Version files that are DOCX/DOC (by extension). */
  get versionFilesDocx(): Array<{
    name: string;
    size?: string;
    isSelected?: boolean;
  }> {
    if (!this.versionFiles?.length) return [];
    return this.versionFiles.filter((f) => {
      const n = (f.name || '').toLowerCase();
      return n.endsWith('.docx') || n.endsWith('.doc');
    });
  }

  get hasViewFilePdf(): boolean {
    return this.versionFilesPdf.length > 0;
  }

  get hasViewFileDocx(): boolean {
    return this.versionFilesDocx.length > 0;
  }

  get hasViewFileMultipleTypes(): boolean {
    return this.hasViewFilePdf && this.hasViewFileDocx;
  }

  /** True when datasetType matches (e.g. 'TEMPORAL') - from root or any resource server. */
  private hasDatasetType(type: string): boolean {
    if (!this.fullDatasetData) return false;
    const upper = type.toUpperCase();
    const rootType = (
      this.fullDatasetData.datasetType ??
      this.fullDatasetData.dataBankType ??
      ''
    )
      .toString()
      .toUpperCase();
    if (rootType === upper) return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some(
      (r: any) => (r?.datasetType ?? '').toString().toUpperCase() === upper,
    );
  }

  /** True when type (e.g. 'ATTR' or 'TEMPORAL') is in resourceServer[].queryTypes or in fullDatasetData.iudxResourceAPIs. */
  private hasQueryType(type: string): boolean {
    if (!this.fullDatasetData) return false;
    const upper = type.toUpperCase();
    const apis = this.fullDatasetData.iudxResourceAPIs;
    if (
      Array.isArray(apis) &&
      apis.some((t: string) => String(t).toUpperCase() === upper)
    )
      return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some((r: any) => {
      const queryTypes = r?.queryTypes;
      return (
        Array.isArray(queryTypes) &&
        queryTypes.some((t: string) => String(t).toUpperCase() === upper)
      );
    });
  }

  get shouldShowGetDataButton(): boolean {
    if (!this.fullDatasetData) return false;
    // NGSI-LD (ATTR) always gets the entity JSON modal
    if (this.hasQueryType('ATTR')) return true;
    // Gateway RS-proxy Get Data popup
    if (this.hasGatewayResourceServer()) return true;
    // Item API with resourceServer: OGC STAC/FEATURE popups, or FILE-only databank download
    if (this.hasNonemptyResourceServerFromItem()) {
      if (this.hasOgcResourceServerWithQueryType('STAC')) return true;
      if (this.hasOgcResourceServerWithQueryType('FEATURE')) return true;
      return this.isItemApiResourceServerOnlyFile();
    }
    return this.hasFileResource();
  }

  /** Item API included a non-empty `resourceServer` field. */
  private hasNonemptyResourceServerFromItem(): boolean {
    const rs = this.fullDatasetData?.resourceServer;
    if (rs == null) return false;
    if (Array.isArray(rs)) return rs.length > 0;
    return typeof rs === 'object';
  }

  /** Normalized `resourceServer` list from the item API. */
  private getItemApiResourceServerList(): any[] {
    if (!this.hasNonemptyResourceServerFromItem()) return [];
    const rs = this.fullDatasetData!.resourceServer;
    return Array.isArray(rs) ? rs : [rs];
  }

  private isFileResourceServerEntry(r: any): boolean {
    if (!r || typeof r !== 'object') return false;
    const name = (r.name ?? '').toString().toUpperCase();
    if (
      name === 'OGC' ||
      name.includes('OGC') ||
      name === 'GATEWAY' ||
      name.includes('GATEWAY') ||
      name.includes('NGSI')
    ) {
      return false;
    }
    const datasetType = (r.datasetType ?? '').toString().toUpperCase();
    const resourceType = (r.resourceType ?? '').toString().toUpperCase();
    const type = (r.type ?? '').toString().toLowerCase();
    if (
      name === 'FILE' ||
      datasetType === 'FILE' ||
      resourceType === 'FILE' ||
      type === 'file'
    ) {
      return true;
    }
    const url = (r.url ?? '').toString().toLowerCase();
    return (
      (url.includes('.file.') && url.includes('iudx.io')) ||
      (url.includes('file-s3') && url.includes('iudx.io'))
    );
  }

  private isItemApiResourceServerOnlyFile(): boolean {
    const list = this.getItemApiResourceServerList();
    if (list.length === 0) return false;
    return list.every((entry) => this.isFileResourceServerEntry(entry));
  }

  /** OGC resource server (name or datasetType) with a matching queryTypes entry â€” e.g. STAC popup flow. */
  private hasOgcResourceServerWithQueryType(queryType: string): boolean {
    const list = this.getItemApiResourceServerList();
    return list.some(
      (s) =>
        this.resourceServerMatchesNameOrType(s, 'OGC') &&
        this.resourceServerHasQueryType(s, queryType),
    );
  }

  private resourceServerMatchesNameOrType(
    server: any,
    target: string,
  ): boolean {
    const upper = target.toUpperCase();
    const name = String(server?.name ?? '').toUpperCase();
    const datasetType = String(server?.datasetType ?? '').toUpperCase();
    return name === upper || name.includes(upper) || datasetType === upper;
  }

  private resourceServerHasQueryType(server: any, queryType: string): boolean {
    const target = queryType.toUpperCase();
    const queryTypes = server?.queryTypes;
    return (
      Array.isArray(queryTypes) &&
      queryTypes.some((t: string) => String(t).toUpperCase().includes(target))
    );
  }

  /** True when FILE is in resourceServer (name, datasetType, or resourceType) or in iudxResourceAPIs. */
  private hasFileResource(): boolean {
    if (!this.fullDatasetData) return false;
    const apis = this.fullDatasetData.iudxResourceAPIs;
    if (
      Array.isArray(apis) &&
      apis.some((t: string) => String(t).toUpperCase() === 'FILE')
    )
      return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some((r: any) => {
      const name = (r?.name ?? '').toString().toUpperCase();
      const datasetType = (r?.datasetType ?? '').toString().toUpperCase();
      const resourceType = (r?.resourceType ?? '').toString().toUpperCase();
      return (
        name === 'FILE' || datasetType === 'FILE' || resourceType === 'FILE'
      );
    });
  }

  /** Show Sample Data and Data descriptors only when datasetType=TEMPORAL and queryTypes includes ATTR. */
  get shouldShowSampleDataAndDescriptors(): boolean {
    return (
      !this.shouldShowViewFile &&
      this.hasDatasetType('TEMPORAL') &&
      this.hasQueryType('ATTR')
    );
  }

  /** True when the item API returned a non-empty dataSample (show Sample Data button and modal). */
  get hasDataSample(): boolean {
    const d = this.sampleData;
    if (d == null) return false;
    if (typeof d === 'string') return d.trim().length > 0;
    if (Array.isArray(d)) return d.length > 0;
    if (typeof d === 'object') return Object.keys(d).length > 0;
    return true;
  }

  /** True when the item API returned dataDescriptor (show Data Descriptors button and modal). */
  get hasDataDescriptor(): boolean {
    return (
      Array.isArray(this.dataDescriptors) && this.dataDescriptors.length > 0
    );
  }

  /** Show Additional Information tab only when GET feedback returned FAQ and/or INFO. */
  get showAdditionalInfoTab(): boolean {
    return this.feedbackFaqRows.length > 0 || this.feedbackInfoText.trim().length > 0;
  }

  toggleFaq(index: number): void {
    this.expandedFaqIndex = this.expandedFaqIndex === index ? null : index;
  }

  private async loadAdditionalInfo(): Promise<void> {
    const id = this.datasetId;
    if (!id) {
      this.feedbackFaqRows = [];
      this.feedbackInfoText = '';
      this.expandedFaqIndex = null;
      this.leaveAdditionalInfoTabIfHidden();
      return;
    }
    const items = await this.catalogueApiService.getProviderFeedback(id);
    if (this.datasetId !== id) {
      return;
    }
    const split = splitFeedbackItems(items);
    this.feedbackFaqRows = split.faqRows;
    this.feedbackInfoText = split.infoText;
    this.expandedFaqIndex = split.faqRows.length > 0 ? 0 : null;
    this.leaveAdditionalInfoTabIfHidden();
    this.cdr.detectChanges();
  }

  private leaveAdditionalInfoTabIfHidden(): void {
    if (this.activeTab !== 'additional-info' || this.showAdditionalInfoTab) {
      return;
    }
    this.activeTab = 'dataset';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'dataset' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Show View File when the item advertises FILE-only via `resourceServer`, or (legacy) FILE with no ATTR/TEMPORAL query paths.
   */
  get shouldShowViewFile(): boolean {
    if (this.hasNonemptyResourceServerFromItem()) {
      return this.isItemApiResourceServerOnlyFile();
    }
    if (!this.hasFileResource()) return false;
    if (this.hasQueryType('ATTR') || this.hasQueryType('TEMPORAL'))
      return false;
    return true;
  }

  /**
   * Get Data: call file-server `v1/databanks/{id}/download` when item `resourceServer` is FILE-only (or legacy FILE-only rule).
   */
  get isFileServerDatabankGetDataFlow(): boolean {
    if (this.hasNonemptyResourceServerFromItem()) {
      if (!this.isItemApiResourceServerOnlyFile()) return false;
      return !this.hasGatewayResourceServer();
    }
    if (!this.shouldShowViewFile) return false;
    return !this.hasGatewayResourceServer();
  }

  /** Any resource server whose name indicates the MahaAgX Gateway Get Data flow (handled separately from databank download). */
  private hasGatewayResourceServer(): boolean {
    const list = this.resourceServersList as ResourceServer[];
    return list.some((s: ResourceServer) => {
      const name = (s?.name ?? '').toString().toUpperCase();
      return name === 'GATEWAY' || name.includes('GATEWAY');
    });
  }

  /** Normalized list of resource servers for display (name, url, queryTypes). */
  get resourceServersList(): any[] {
    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer) {
      return [];
    }
    const rs = this.fullDatasetData.resourceServer;
    return Array.isArray(rs) ? rs : [rs];
  }

  /**
   * Resource servers shown in the details table.
   * FILE-only datasets: show FILE. NGSI-LD/OGC + FILE: hide FILE row (file access via Get Data / View File).
   */
  get displayResourceServersList(): any[] {
    const list = this.resourceServersList;
    if (list.length === 0) return [];
    const hasNgsiOrOgc = list.some((entry) =>
      this.isNgsiLdOrOgcResourceServerEntry(entry),
    );
    if (!hasNgsiOrOgc) return list;
    return list.filter((entry) => !this.isFileResourceServerEntry(entry));
  }

  private isNgsiLdOrOgcResourceServerEntry(r: any): boolean {
    if (!r || typeof r !== 'object') return false;
    const name = (r.name ?? '').toString().toUpperCase();
    const datasetType = (r.datasetType ?? '').toString().toUpperCase();
    if (name === 'OGC' || name.includes('OGC') || datasetType === 'OGC')
      return true;
    return (
      name.includes('NGSI') || name === 'NGSI-LD' || datasetType === 'NGSI-LD'
    );
  }

  /** Resource server section layout: 'cards' | 'table' | 'list' (attribute-style rows). */
  resourceServerDesign: 'cards' | 'table' | 'list' = 'table';

  async ngOnInit(): Promise<void> {
    // Read tab from query parameters FIRST (before login check to preserve it)
    let tabFromUrl: string | null = null;
    try {
      const queryParams = await firstValueFrom(this.route.queryParams);
      tabFromUrl = queryParams['tab'] as string;
      console.log('[Tab Persistence] Tab from URL:', tabFromUrl);
    } catch (error) {
      console.error('[Tab Persistence] Error reading query params:', error);
    }

    this.route.queryParamMap.subscribe((queryParamMap) => {
      this.itemAuditEnabled = parseItemAuditEnabled(queryParamMap);
    });

    // Check login status on initialization - wait a bit for Keycloak to initialize
    try {
      // Give Keycloak a moment to initialize if needed
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Try multiple methods to check login status
      let isLoggedInCheck = false;
      try {
        isLoggedInCheck = await this.keycloak.isLoggedIn();
      } catch (e) {
        console.log(
          '[Tab Persistence] Keycloak.isLoggedIn() failed, trying alternative check',
        );
      }

      // Fallback: Check if we can get the token from Keycloak
      if (!isLoggedInCheck) {
        try {
          const token = await this.keycloak.getToken();
          isLoggedInCheck = !!token;
        } catch (e) {
          // If that fails, check localStorage
          const localToken = localStorage.getItem('ai-sandbox-cat-auth-token');
          isLoggedInCheck = !!localToken;
        }
      }

      this.isLoggedIn = isLoggedInCheck;
      console.log('[Tab Persistence] Login status:', this.isLoggedIn);
      if (this.isLoggedIn) {
        await this.loadCurrentUserInfo();
      }
    } catch (error) {
      console.error('Error checking login status:', error);
      // Fallback: Check localStorage for token
      const token = localStorage.getItem('ai-sandbox-cat-auth-token');
      this.isLoggedIn = !!token;
      console.log(
        '[Tab Persistence] Using localStorage fallback, isLoggedIn:',
        this.isLoggedIn,
      );
      if (this.isLoggedIn) {
        await this.loadCurrentUserInfo();
      }
    }

    // Now restore the tab based on URL and login status
    if (tabFromUrl && DATASET_DETAIL_TABS.includes(tabFromUrl as DatasetDetailTab)) {
      const tab = tabFromUrl as DatasetDetailTab;
      
      // Check if tab requires login
      const requiresLogin =
        tab === 'details' ||
        tab === 'visualisation' ||
        tab === 'sql-playground';

      if (requiresLogin && !this.isLoggedIn) {
        // If tab requires login but user is not logged in, stay on dataset tab
        console.log(
          '[Tab Persistence] Tab requires login but user not logged in, defaulting to dataset',
        );
        this.activeTab = 'dataset';
        // Only update URL if it's different from what we want
        if (tabFromUrl !== 'dataset') {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: 'dataset' },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      } else {
        // Set the tab and load data if needed
        console.log(
          '[Tab Persistence] Setting active tab to:',
          tab,
          'isLoggedIn:',
          this.isLoggedIn,
        );
        this.activeTab = tab;
        if (tab === 'discussion') {
          this.loadDatasetDiscussions();
        } else if (tab === 'details' || tab === 'visualisation') {
          this.loadDetailsTabData();
        }
        // Don't update URL - it's already correct, preserve the tab parameter
      }
    } else {
      // No tab in URL, set default and add it
      if (!tabFromUrl) {
        console.log(
          '[Tab Persistence] No tab in URL, setting default to dataset',
        );
        this.activeTab = 'dataset';
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: 'dataset' },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    }

    this.route.paramMap.subscribe((params) => {
      const nextId = params.get('id');
      if (nextId !== this.datasetId) {
        this.accessRequestPending = false;
        this.accessStatus = {};
      }
      this.datasetId = nextId;
      console.log(
        'Dataset Detail Component initialized with ID:',
        this.datasetId,
      );
      if (!this.datasetId) {
        console.error('No dataset ID found in route parameters');
        return;
      }
      // Load dataset details from API using this.datasetId
      // loadSimilarDatasets() is called inside loadDatasetDetails() after tags are loaded
      this.loadDatasetDetails();
      void this.loadAdditionalInfo();
    });

    // Subscribe to Keycloak events to call hasAccess when user logs in
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          // User has logged in, update login status and call hasAccess API
          this.isLoggedIn = true;
          this.hasLoadedCurrentUserInfo = false;
          await this.loadCurrentUserInfo(true);
          console.log('User logged in, calling checkUserAccess');
          await this.checkUserAccess();
          await Promise.all([this.checkBookmarkAndLikeStatus(), this.loadUserFeedback()]);

          // If there's a pending tab, switch to it after login
          await this.onLoginSuccess();
        } else if (keycloakEvent.type === KeycloakEventType.OnAuthLogout) {
          // User has logged out, update login status
          this.isLoggedIn = false;
          this.currentUserSub = null;
          this.currentUserEmail = null;
          this.hasLoadedCurrentUserInfo = false;
          this.isBookmarked = false;
          this.isLiked = false;
          this.pendingTab = null; // Clear any pending tab on logout
        }
      },
    );
  }

  async setActiveTab(tab: DatasetDetailTab): Promise<void> {
    // Check if user needs to be logged in for this tab
    const requiresLogin =
      tab === 'details' || tab === 'visualisation' || tab === 'sql-playground';

    if (requiresLogin) {
      // Check if user is logged in
      try {
        const isLoggedIn = await this.keycloak.isLoggedIn();
        this.isLoggedIn = isLoggedIn;

        if (!isLoggedIn) {
          // Store the tab the user wanted to access
          this.pendingTab = tab;
          // Show login modal instead of switching tabs
          this.showLoginModal = true;
          return;
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        // On error, store the tab and show login modal to be safe
        this.pendingTab = tab;
        this.showLoginModal = true;
        return;
      }
    }

    // User is logged in or tab doesn't require login, proceed with tab switch
    this.activeTab = tab;

    // Update URL query parameters to persist tab state
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (tab === 'discussion') {
      this.loadDatasetDiscussions();
    } else if (
      tab === 'details' ||
      tab === 'visualisation' ||
      tab === 'sql-playground'
    ) {
      this.loadDetailsTabData();
    }
  }

  /**
   * Handle successful login - switch to pending tab if one exists
   */
  async onLoginSuccess(): Promise<void> {
    // Update login status
    this.isLoggedIn = true;

    // If there's a pending tab, switch to it
    if (this.pendingTab) {
      const tabToSwitch = this.pendingTab;
      this.pendingTab = null; // Clear pending tab

      // Switch to the tab the user originally wanted
      this.activeTab = tabToSwitch;

      // Update URL query parameters to persist tab state
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabToSwitch },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });

      if (tabToSwitch === 'discussion') {
        this.loadDatasetDiscussions();
      } else if (
        tabToSwitch === 'details' ||
        tabToSwitch === 'visualisation' ||
        tabToSwitch === 'sql-playground'
      ) {
        this.loadDetailsTabData();
      }
    }
  }

  /**
   * Extract files array from files API response. Handles multiple shapes:
   * - { files: [...] }
   * - { data: { files: [...] } }
   * - { success: true, data: { files: [...], directories: [] } }
   */
  private extractFilesFromFilesResponse(data: any): any[] {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.files)) return data.files;
    const inner = data.data;
    if (inner && typeof inner === 'object' && Array.isArray(inner.files))
      return inner.files;
    return [];
  }

  async loadDetailsTabData(): Promise<void> {
    // Load version files and preview data for Details tab
    this.detailsTabDataLoading = true;

    try {
      // Call getFilesForPreview API if datasetId is available
      if (this.datasetId) {
        try {
          const filesResponse = await this.fileApiService.getFilesForPreview(
            this.datasetId,
          );
          console.log('Files for preview response:', filesResponse);

          // Extract files array from various response shapes (flat, nested, or success.data.files)
          const apiFiles = this.extractFilesFromFilesResponse(
            filesResponse?.data,
          );

          // Process the API response
          if (apiFiles.length >= 0) {
            // Map API files to component structure
            const files: Array<{
              name: string;
              size?: string;
              isSelected?: boolean;
              lastModified?: string;
            }> = [];

            if (apiFiles.length > 0) {
              let totalSizeBytes = 0;
              let latestModifiedDate: Date | null = null;

              apiFiles.forEach((file: any, index: number) => {
                // Only process files (not directories)
                if (file.isFile !== false) {
                  const fileName = file.key || `file_${index + 1}`;
                  const fileSizeBytes = file.size || 0;
                  totalSizeBytes += fileSizeBytes;

                  // Track latest modified date
                  if (file.lastModified) {
                    const modifiedDate = new Date(file.lastModified);
                    if (
                      !latestModifiedDate ||
                      modifiedDate > latestModifiedDate
                    ) {
                      latestModifiedDate = modifiedDate;
                    }
                  }

                  files.push({
                    name: fileName,
                    size:
                      fileSizeBytes > 0
                        ? this.formatFileSize(fileSizeBytes)
                        : undefined,
                    // Default-select the first file so the right pane isn't blank.
                    isSelected: files.length === 0,
                    lastModified: file.lastModified,
                  });
                }
              });

              this.versionFiles = files;

              // Set total size
              this.versionFilesTotalSize =
                totalSizeBytes > 0 ? this.formatFileSize(totalSizeBytes) : '';

              // Set last updated from the latest file modification date
              if (latestModifiedDate) {
                const dateValue = latestModifiedDate as Date;
                this.versionFilesLastUpdated =
                  this.formatService.formatDate(dateValue.toISOString()) || '';
              } else {
                this.versionFilesLastUpdated =
                  this.datasetDetails.lastUpdated ||
                  this.datasetCardDetails.createdAt ||
                  '';
              }

              // Sync preview state (selected file will be loaded if needed).
              this.loadPreviewForSelectedFileIfNeeded();
              this.cdr.markForCheck();
            } else {
              // No files found
              this.versionFiles = [];
              this.versionFilesTotalSize = '';
              this.versionFilesLastUpdated = '';
              this.versionFilePreviews = {};
              this.detailsPreviewHeaders = [];
              this.detailsPreviewEntries = [];
              this.fileMetadata = null;
            }
            this.cdr.markForCheck();
          }
        } catch (error) {
          console.error('Error fetching files for preview:', error);
          // On error, set empty files
          this.versionFiles = [];
          this.versionFilesTotalSize = '';
          this.versionFilesLastUpdated = '';
          this.versionFilePreviews = {};
          this.detailsPreviewHeaders = [];
          this.detailsPreviewEntries = [];
          this.fileMetadata = null;
        }
      } else {
        // No datasetId available
        this.versionFiles = [];
        this.versionFilesTotalSize = '';
        this.versionFilesLastUpdated = '';
        this.versionFilePreviews = {};
        this.detailsPreviewHeaders = [];
        this.detailsPreviewEntries = [];
        this.fileMetadata = null;
      }

      // Do not auto-call entity API (ngsi-ld/v2/entities) when Details tab loads.
      // Entity data is loaded only when the user clicks "Get Data" and opens the Get Data modal.
    } catch (error) {
      console.error('Error loading details tab data:', error);
      // On error, set empty files
      this.versionFiles = [];
      this.versionFilesTotalSize = '';
      this.versionFilesLastUpdated = '';
      this.versionFilePreviews = {};
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
      this.fileMetadata = null;
    } finally {
      this.detailsTabDataLoading = false;
    }
  }

  private getSelectedVersionFileNameLc(): string {
    const selected = this.versionFiles.find((f) => f.isSelected);
    return (selected?.name || '').toLowerCase();
  }

  get isSelectedVersionFileDoc(): boolean {
    const n = this.getSelectedVersionFileNameLc();
    return n.endsWith('.doc') && !n.endsWith('.docx');
  }

  get isSelectedVersionFileDocx(): boolean {
    const n = this.getSelectedVersionFileNameLc();
    return n.endsWith('.docx');
  }

  /**
   * Extract headers and rows from a preview API response (same shape as Visualisation tab).
   * Handles data.content.preview, data.content, and success/data wrapper.
   */
  private extractPreviewTable(
    previewRes: { data: any; status: number } | null,
  ): { headers: string[]; entries: any[] } {
    const empty = { headers: [] as string[], entries: [] as any[] };
    if (!previewRes?.data || typeof previewRes.data !== 'object') return empty;

    let actualData = previewRes.data;
    if (previewRes.data?.success && previewRes.data?.data !== undefined) {
      actualData = previewRes.data.data;
    }
    if (actualData?.data?.content !== undefined) {
      actualData = {
        content: actualData.data.content,
        format: actualData.data.format || actualData.format,
      };
    }

    const content =
      actualData?.content || (actualData?.headers ? actualData : null);
    if (!content) return empty;

    // API may put headers/data in content.preview (e.g. xlsx)
    const block =
      content.preview && typeof content.preview === 'object'
        ? content.preview
        : content;
    const headers: string[] = Array.isArray(block?.headers)
      ? block.headers
      : [];
    const data = block?.data;
    const entries = Array.isArray(data) ? data : [];

    return { headers, entries };
  }

  /** Set detailsPreviewHeaders and detailsPreviewEntries from the currently selected file's preview. */
  syncDetailsPreviewFromSelectedFile(): void {
    const selected = this.versionFiles.find((f) => f.isSelected);
    if (!selected) {
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
      return;
    }
    const previewRes = this.versionFilePreviews[selected.name];
    const { headers, entries } = this.extractPreviewTable(previewRes ?? null);
    this.detailsPreviewHeaders = headers;
    this.detailsPreviewEntries = entries;
  }

  /** Load preview for the currently selected file if not already cached. */
  async loadPreviewForSelectedFileIfNeeded(): Promise<void> {
    const selected = this.versionFiles.find((f) => f.isSelected);
    if (!selected || !this.datasetId) {
      this.syncDetailsPreviewFromSelectedFile();
      this.cdr.markForCheck();
      return;
    }
    if (this.versionFilePreviews[selected.name]) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }
    this.detailsPreviewLoading = true;
    this.cdr.markForCheck();
    try {
      const previewRes = await this.fileApiService.generatePreview(
        this.datasetId,
        selected.name,
      );
      this.versionFilePreviews[selected.name] = previewRes;
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
    } catch (err) {
      console.warn('Preview failed for', selected.name, err);
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
    } finally {
      this.detailsPreviewLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Load file metadata for the currently selected version file. Displayed below Version Files section. */
  async loadFileMetadataForSelectedFile(): Promise<void> {
    const selected = this.versionFiles.find((f) => f.isSelected);
    if (!selected || !this.datasetId) {
      this.fileMetadata = null;
      this.cdr.markForCheck();
      return;
    }
    this.fileMetadataLoading = true;
    this.fileMetadata = null;
    this.cdr.markForCheck();
    try {
      const res = await this.fileApiService.getFileMetadata(this.datasetId, {
        key: selected.name,
      });
      const raw = res?.data;
      // Handle success/data wrapper (e.g. { success: true, data: { ... } })
      this.fileMetadata = raw?.success && raw?.data != null ? raw.data : raw;
    } catch (err) {
      console.warn('File metadata failed for', selected.name, err);
      this.fileMetadata = null;
    } finally {
      this.fileMetadataLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Called when user clicks a file in the Details tab Version Files list. Loads preview only for that file. */
  async onDetailsFileSelect(file: {
    name: string;
    size?: string;
    isSelected?: boolean;
  }): Promise<void> {
    this.versionFiles = this.versionFiles.map((f) => ({
      ...f,
      isSelected: f.name === file.name,
    }));
    this.scrollToTabView();

    const cached = this.versionFilePreviews[file.name];
    if (cached) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }

    if (!this.datasetId) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }

    this.detailsPreviewLoading = true;
    this.cdr.markForCheck();

    try {
      const previewRes = await this.fileApiService.generatePreview(
        this.datasetId,
        file.name,
      );
      this.versionFilePreviews[file.name] = previewRes;
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
    } catch (err) {
      console.warn('Preview failed for', file.name, err);
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
    } finally {
      this.detailsPreviewLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Scroll the page so the tab row (Dataset, Details, Visualisation, etc.) is in view, below the header. */
  private scrollToTabView(): void {
    setTimeout(() => {
      const tabsEl = document.getElementById('dataset-detail-tabs');
      if (!tabsEl) return;
      const headerOffset = 100;
      const rect = tabsEl.getBoundingClientRect();
      const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - headerOffset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }, 0);
  }

  /** File size for selected file: from metadata (bytes â†’ MB) or fallback to aggregate. */
  get selectedFileSizeDisplay(): string {
    if (this.fileMetadata?.size != null) {
      const bytes =
        typeof this.fileMetadata.size === 'string'
          ? parseInt(this.fileMetadata.size, 10)
          : this.fileMetadata.size;
      return !isNaN(bytes)
        ? this.formatFileSize(bytes)
        : this.versionFilesTotalSize;
    }
    return this.versionFilesTotalSize;
  }

  /** Last updated for selected file: from metadata (ISO â†’ DD-MMM-YYYY HH:MM) or fallback. */
  get selectedFileLastUpdatedDisplay(): string {
    if (this.fileMetadata?.lastModified) {
      return (
        this.formatService.formatDate(this.fileMetadata.lastModified) ||
        this.versionFilesLastUpdated
      );
    }
    return this.versionFilesLastUpdated;
  }

  /** Name of the currently selected version file, for viewer title. */
  get selectedVersionFileName(): string {
    if (!this.versionFiles || this.versionFiles.length === 0) {
      return '';
    }
    const selected = this.versionFiles.find((f) => f.isSelected);
    return selected?.name || '';
  }

  getDetailsTableValue(entry: any, header: string): string {
    const value =
      entry != null && typeof entry === 'object' && header in entry
        ? entry[header]
        : undefined;
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getTableHeaders(): string[] {
    if (!this.getDataEntries || this.getDataEntries.length === 0) {
      return [];
    }

    const headerSet = new Set<string>();
    this.getDataEntries.forEach((entry) => {
      this.flattenObject(entry, '', headerSet);
    });

    // Return first 10 headers for preview
    return Array.from(headerSet).slice(0, 10);
  }

  getTableValue(entry: any, header: string): string {
    let value = this.getNestedValue(entry, header);
    if (
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'value' in value
    ) {
      value = (value as { value: unknown }).value;
    }
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  async loadDatasetDiscussions(): Promise<void> {
    if (!this.datasetId) {
      this.datasetDiscussions = [];
      return;
    }
    this.datasetDiscussionsLoading = true;
    this.datasetDiscussionsError = null;
    this.cdr.detectChanges();
    try {
      const response = await this.discussionApiService.getDiscussionChoices({
        filters: {
          type: [],
          status: ['APPROVED'],
          tags: [],
          sub_category_id: this.datasetId,
        },
        pinned: true,
        sort_by: 'newest',
        page: 1,
        limit: 10,
      });
      const discussions =
        response?.data?.discussions ?? response?.discussions ?? [];
      this.datasetDiscussions = (
        Array.isArray(discussions) ? discussions : []
      ).map((item: any) => this.mapDiscussionChoiceToItem(item));
    } catch (error) {
      console.error('Error loading dataset discussions:', error);
      this.datasetDiscussionsError =
        'Failed to load discussions. Please try again.';
      this.datasetDiscussions = [];
    } finally {
      this.datasetDiscussionsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapDiscussionChoiceToItem(item: any): DiscussionItem {
    const author = item?.user?.name ?? 'Unknown';
    const subCategory = item?.sub_category ?? this.headerConfig.title ?? '';
    const publishedTime = item?.published_time ?? item?.created_at ?? '';
    return {
      id: item?.id ?? '',
      title: item?.title ?? '',
      description: `${author} Â· ${subCategory}`,
      author,
      category: item?.category ?? 'DATA_BANKS',
      replies: item?.votes ?? 0,
      views: item?.view_count ?? 0,
      lastActivity: publishedTime
        ? `Published ${this.formatService.formatDiscussionActivity(publishedTime)}`
        : '',
      isPinned: item?.is_pinned ?? false,
      isBookmarked: item?.is_bookmarked ?? false,
      isVoted: item?.is_voted ?? false,
      status: item?.status,
      subCategoryId: item?.sub_category_id,
      assetType: 'Dataset',
      assetName: subCategory,
    };
  }

  navigateToDiscussion(discussionId: string): void {
    if (this.isAssetReadOnly) {
      return;
    }

    this.discussionApiService
      .getReview(discussionId)
      .then(() => {
        this.router.navigate(['/discussions', discussionId]);
      })
      .catch(() => {
        this.router.navigate(['/discussions', discussionId]);
      });
  }

  async createNewDiscussion(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.router.navigate(['/discussions/create-new'], {
      queryParams: { category: 'GETTING_STARTED', tab: 'getting-started' },
    });
  }

  closeCreateDiscussionOffcanvas(): void {
    this.showCreateDiscussionOffcanvas = false;
  }

  onDiscussionPublish(formData: DiscussionFormData): void {
    // TODO: Implement API call to publish discussion
    console.log('Publishing discussion:', formData);
    // Close offcanvas and reload discussions
    this.closeCreateDiscussionOffcanvas();
    this.loadDatasetDiscussions();
  }

  onDiscussionClose(): void {
    this.closeCreateDiscussionOffcanvas();
  }

  get discussionInitialValues(): Partial<DiscussionFormData> {
    return {
      assetCategory: 'Dataset',
      assetId: this.datasetId || '',
      assetName: this.headerConfig.title || '',
    };
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  /**
   * Handle bookmark button click
   */
  async onBookmarkClick(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.datasetId) {
      console.error('Cannot bookmark: datasetId is null');
      return;
    }

    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';

      const payload = {
        assetId: this.datasetId,
        assetType: 'DATABANK',
        actionType: actionType,
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);

      // Update local state
      this.isBookmarked = !this.isBookmarked;
      if (this.isBookmarked) {
        this.toasterService.success(
          "You've bookmarked the Dataset successfully.",
        );
      } else {
        this.toasterService.success('Bookmark removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  /**
   * Handle like button click
   */
  async onLikeClick(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.datasetId) {
      console.error('Cannot like: datasetId is null');
      return;
    }

    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';

      const payload = {
        assetId: this.datasetId,
        assetType: 'DATABANK',
        actionType: actionType,
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);

      // Update local state
      this.isLiked = !this.isLiked;
      // Optimistically update like count immediately (item API may have eventual consistency)
      this.activityMetrics.likes = Math.max(
        0,
        (this.activityMetrics.likes ?? 0) + (this.isLiked ? 1 : -1),
      );
      if (this.isLiked) {
        this.toasterService.success("You've liked the Dataset successfully.");
      } else {
        this.toasterService.success("You've Unliked the Dataset successfully.");
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  /** Refresh activity metrics from item API (e.g. after like/bookmark). Use API values as-is; calling getItemById may increment view on server, so prefer optimistic update for like and avoid calling this after like to prevent extra view count. */
  private async refreshActivityMetrics(): Promise<void> {
    if (!this.datasetId) return;
    try {
      const response = await this.catalogueApiService.getItemById(
        this.datasetId,
        {
          auditEnabled: false,
        },
      );
      let data = response;
      if (response?.result)
        data = Array.isArray(response.result)
          ? response.result[0]
          : response.result;
      else if (response?.data)
        data = Array.isArray(response.data) ? response.data[0] : response.data;
      else if (response?.items?.[0]) data = response.items[0];
      else if (Array.isArray(response) && response.length > 0)
        data = response[0];
      if (!data) return;
      const metrics = data.metrics || data;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
      this.activityMetrics.rating = this.readAverageRating(metrics);
    } catch {
      // Ignore - keep existing metrics
    }
  }

  /**
   * Check if the dataset is bookmarked and liked by the user
   */
  async checkBookmarkAndLikeStatus(): Promise<void> {
    if (!this.isLoggedIn || !this.datasetId) {
      return;
    }

    try {
      // Fetch all user interactions for this assetId on page load
      const response = await this.userInteractionApiService.userInteractions({
        assetId: this.datasetId,
        assetType: 'DATABANK',
      });

      // Extract items from possible response shapes
      const items: any[] =
        (response?.result && Array.isArray(response.result)
          ? response.result
          : null) ||
        (response?.results && Array.isArray(response.results)
          ? response.results
          : null) ||
        (response?.data && Array.isArray(response.data)
          ? response.data
          : null) ||
        [];

      // API may return: result[0].userInteraction.{ bookmarked, liked } (new) or result[0].assetDetails / top-level bookmarked, liked (legacy)
      const first = items[0] ?? {};
      const userInteraction = first?.userInteraction;
      const ad = first?.assetDetails ?? first;

      const bookmarked =
        typeof userInteraction?.bookmarked === 'boolean'
          ? userInteraction.bookmarked
          : typeof first?.isBookmarked === 'boolean'
            ? first.isBookmarked
            : typeof first?.bookmarked === 'boolean'
              ? first.bookmarked
              : typeof ad?.bookmarked === 'boolean'
                ? ad.bookmarked
                : null;
      if (bookmarked !== null) {
        this.isBookmarked = bookmarked;
      } else {
        const actionTypes = items
          .map((it: any) =>
            (it?.actionType ?? it?.type ?? it?.interactionType ?? '')
              .toString()
              .toUpperCase(),
          )
          .filter(Boolean);
        this.isBookmarked = actionTypes.includes('BOOKMARK');
      }

      const liked =
        typeof userInteraction?.liked === 'boolean'
          ? userInteraction.liked
          : typeof first?.isLiked === 'boolean'
            ? first.isLiked
            : typeof first?.liked === 'boolean'
              ? first.liked
              : typeof ad?.liked === 'boolean'
                ? ad.liked
                : null;
      if (liked !== null) {
        this.isLiked = liked;
      } else {
        const actionTypes = items
          .map((it: any) =>
            (it?.actionType ?? it?.type ?? it?.interactionType ?? '')
              .toString()
              .toUpperCase(),
          )
          .filter(Boolean);
        this.isLiked = actionTypes.includes('LIKE');
      }

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking bookmark/like status:', error);
      // Don't show error to user - silently fail
    }
  }

  /**
   * Load approved ratings for this dataset
   * (GET /iudx/v2/user/feedback/approved?assetId=&sort=ratingCreatedAt:desc&rating=).
   */
  async loadUserFeedback(): Promise<void> {
    if (!this.datasetId) {
      this.userFeedbackItems = [];
      this.userFeedbackSummary = null;
      this.myDatasetReview = null;
      this.ownSubmittedReview = null;
      return;
    }

    const request = ++this.userFeedbackRequest;
    const rating = this.userRatingsFilter;
    try {
      const ownPromise = this.fetchOwnDatasetFeedback();
      const response = await this.userInteractionApiService.getApprovedUserFeedback({
        assetId: this.datasetId,
        page: 1,
        size: 100,
        sort: this.userRatingsSort === 'new' ? 'ratingCreatedAt:desc' : 'ratingCreatedAt:asc',
        ...(rating != null ? { rating } : {}),
      });
      const own = await ownPromise;
      if (request !== this.userFeedbackRequest) {
        return;
      }
      this.ownSubmittedReview = own;
      const items = this.extractFeedbackList(response);
      const mapped = items.map((item) => this.mapApprovedDatasetReview(item));
      const mine = this.findMyDatasetReview(mapped);
      if (mine) {
        this.myDatasetReview = mine;
      } else if (rating == null) {
        this.myDatasetReview = null;
      }
      const hasExplicitStatus = items.some((item) =>
        this.hasExplicitReviewStatus(this.asFeedbackRecord(item)),
      );
      this.userFeedbackItems = hasExplicitStatus
        ? mapped.filter((item) => item.status === 'approved')
        : mapped.filter((item) => item.status !== 'rejected');
      if (rating == null) {
        this.userFeedbackSummary =
          this.extractFeedbackSummary(response) ?? this.summarizeDatasetReviews(this.userFeedbackItems);
      }
      this.userRatingsPage = 0;
      this.cdr.detectChanges();
      this.openPendingRatingEditorAfterLogin();
    } catch (error) {
      if (request !== this.userFeedbackRequest) {
        return;
      }
      console.error('Error loading user feedback:', error);
      this.userFeedbackItems = [];
      if (rating == null) {
        this.userFeedbackSummary = null;
        this.myDatasetReview = null;
      }
    }
  }

  get hasUserRatings(): boolean {
    return (this.userFeedbackSummary?.totalRatings ?? 0) > 0 || this.userFeedbackItems.length > 0;
  }

  get hasOwnRating(): boolean {
    return !!this.myDatasetReview;
  }

  /** Edit is only for a rating that is still pending. An approved rating stays read-only. */
  get hasOwnPendingRating(): boolean {
    if (this.myDatasetReview?.status === 'approved' || this.ownSubmittedReview?.status === 'approved') {
      return false;
    }
    return (this.ownSubmittedReview?.status ?? this.myDatasetReview?.status) === 'pending';
  }

  /** Approved or pending feedback already exists, so a new rating must not be submitted. */
  get blockNewRating(): boolean {
    const status = this.ownSubmittedReview?.status ?? this.myDatasetReview?.status;
    return status === 'pending' || status === 'approved';
  }

  get giveRatingCtaLabel(): string {
    if (this.hasOwnPendingRating) {
      return 'Edit Your Rating';
    }
    return this.hasOwnRating ? 'View your rating' : 'Give your Rating';
  }

  get giveRatingModalHeader(): string {
    if (this.isEditingOwnRating) {
      return 'Edit Your Rating';
    }
    return this.isViewingOwnRating ? 'View your Rating' : 'Give your Rating';
  }

  get userRatingsFilterLabel(): string {
    if (this.userRatingsFilter == null) {
      return 'All ratings';
    }
    return this.userRatingsFilter === 1 ? '1 star' : `${this.userRatingsFilter} stars`;
  }

  get filteredUserReviews(): DatasetReviewItem[] {
    return this.userFeedbackItems;
  }

  get userRatingsPageCount(): number {
    return Math.max(1, Math.ceil(this.filteredUserReviews.length / this.userRatingsPageSize));
  }

  get userRatingsPageIndexes(): number[] {
    return Array.from({ length: this.userRatingsPageCount }, (_, index) => index);
  }

  get visibleUserReviews(): DatasetReviewItem[] {
    const maxPage = this.userRatingsPageCount - 1;
    const page = Math.min(Math.max(this.userRatingsPage, 0), maxPage);
    const start = page * this.userRatingsPageSize;
    return this.filteredUserReviews.slice(start, start + this.userRatingsPageSize);
  }

  get userRatingsAverage(): number | null {
    return this.userFeedbackSummary?.averageRating ?? this.summarizeDatasetReviews(this.userFeedbackItems)?.averageRating ?? null;
  }

  get userRatingsCount(): number {
    return this.userFeedbackSummary?.totalRatings ?? this.userFeedbackItems.length;
  }

  get userRatingsAverageDisplay(): string {
    const rating = this.userRatingsAverage;
    if (rating == null) {
      return '—';
    }
    return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
  }

  userRatingsStarFilled(star: number, value: number | null = this.userRatingsAverage): boolean {
    return (value ?? 0) >= star;
  }

  ratingStarFillPercent(star: number): number {
    return ratingStarFraction(star, this.userRatingsAverage) * 100;
  }

  setUserRatingsSort(sort: 'new' | 'old'): void {
    if (this.userRatingsSort === sort) {
      return;
    }
    this.userRatingsSort = sort;
    this.userRatingsPage = 0;
    void this.loadUserFeedback();
  }

  toggleUserRatingsSort(): void {
    this.setUserRatingsSort(this.userRatingsSort === 'new' ? 'old' : 'new');
  }

  toggleUserRatingsFilterMenu(): void {
    this.showUserRatingsFilterMenu = !this.showUserRatingsFilterMenu;
  }

  setUserRatingsFilter(stars: number | null): void {
    this.showUserRatingsFilterMenu = false;
    if (this.userRatingsFilter === stars) {
      return;
    }
    this.userRatingsFilter = stars;
    this.userRatingsPage = 0;
    void this.loadUserFeedback();
  }

  goToUserRatingsPage(page: number): void {
    const max = this.userRatingsPageCount - 1;
    this.userRatingsPage = Math.min(Math.max(page, 0), max);
  }

  /** Rows from the approved-feedback API are published even when status is omitted. */
  private mapApprovedDatasetReview(item: unknown): DatasetReviewItem {
    const review = this.mapDatasetReview(item);
    if (!this.hasExplicitReviewStatus(this.asFeedbackRecord(item))) {
      review.status = 'approved';
    }
    return review;
  }

  private hasExplicitReviewStatus(source: Record<string, unknown>): boolean {
    return !!this.readFeedbackString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]);
  }

  private mapDatasetReview(item: unknown): DatasetReviewItem {
    const source = this.asFeedbackRecord(item);
    const user = this.asFeedbackRecord(source['user'] ?? source['consumer'] ?? source['createdBy']);
    const name =
      this.readFeedbackString(source, ['userName', 'consumerName', 'fullName', 'name']) ||
      this.readFeedbackString(user, ['name', 'fullName', 'userName']) ||
      this.readFeedbackString(source, ['userEmail', 'email']) ||
      'Anonymous';
    const email =
      this.readFeedbackString(source, ['userEmail', 'email']) ||
      this.readFeedbackString(user, ['email', 'userEmail']);
    const comment =
      this.readFeedbackString(source, ['comment', 'feedback', 'userFeedback', 'review']) ||
      this.readFeedbackCommentFromSubdata(source['actionSubdata']);
    const createdAtRaw = this.readFeedbackString(source, [
      'ratingCreatedAt',
      'ratingUpdatedAt',
      'createdAt',
      'created_at',
      'updatedAt',
      'submittedAt',
    ]);
    const createdAtMs = createdAtRaw ? new Date(createdAtRaw).getTime() : 0;
    const ratingValue = Number(source['entityRating'] ?? source['rating'] ?? source['stars'] ?? 0);

    return {
      id: this.readFeedbackString(source, ['id', 'feedbackId', '_id']),
      userId: this.readFeedbackString(source, ['userId', 'user_id']) || this.readFeedbackString(user, ['id', 'userId', 'sub']),
      email,
      name,
      organisation: this.readFeedbackString(source, [
        'organisationId',
        'organisation',
        'orgName',
        'organization',
      ]),
      initials: this.reviewInitials(name),
      comment,
      ratingValue: Number.isFinite(ratingValue) ? ratingValue : 0,
      createdAt: createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '',
      createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
      status: this.readReviewStatus(source),
    };
  }

  private readReviewStatus(source: Record<string, unknown>): 'pending' | 'approved' | 'rejected' {
    const raw = this.readFeedbackString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]).toLowerCase();
    if (raw === 'approved' || raw === 'granted') {
      return 'approved';
    }
    if (raw === 'rejected' || raw === 'denied') {
      return 'rejected';
    }
    return 'pending';
  }

  private async fetchOwnDatasetFeedback(): Promise<DatasetReviewItem | null> {
    try {
      if (!(await this.keycloak.isLoggedIn()) || !this.datasetId) {
        return null;
      }
      const response = await this.userInteractionApiService.getMyUserFeedback({
        assetId: this.datasetId,
        page: 1,
        size: 100,
      });
      const match = this.extractFeedbackList(response)
        .map((item) => ({
          source: this.asFeedbackRecord(item),
          review: this.mapDatasetReview(item),
        }))
        .filter(({ source }) => {
          const assetId = this.readFeedbackString(source, ['assetId', 'asset_id']);
          return !assetId || assetId === this.datasetId;
        })
        .map(({ review }) => review)
        .find((review) => review.status === 'pending' || review.status === 'approved');
      return match ?? null;
    } catch {
      return this.ownSubmittedReview;
    }
  }

  private findMyDatasetReview(items: DatasetReviewItem[]): DatasetReviewItem | null {
    const userId = this.currentUserSub ?? this.getCurrentUserSubFromKeycloak();
    const email = (this.currentUserEmail ?? this.getCurrentUserEmailFromKeycloak() ?? '').toLowerCase();
    return (
      items.find((item) => {
        if (userId && item.userId && item.userId === userId) {
          return true;
        }
        if (email && (item.email.toLowerCase() === email || item.name.toLowerCase() === email)) {
          return true;
        }
        return false;
      }) ?? null
    );
  }

  private reviewInitials(name: string): string {
    const local = name.includes('@') ? name.split('@')[0] : name;
    const parts = local.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase() || 'NA';
  }

  private extractFeedbackList(response: unknown): unknown[] {
    const record = this.asFeedbackRecord(response);
    if (Array.isArray(record['result'])) {
      return record['result'];
    }
    const nestedResult = this.asFeedbackRecord(record['result']);
    if (Array.isArray(nestedResult['data'])) {
      return nestedResult['data'];
    }
    if (Array.isArray(record['results'])) {
      return record['results'];
    }
    if (Array.isArray(record['data'])) {
      return record['data'];
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private extractFeedbackSummary(
    response: unknown,
  ): { averageRating: number | null; totalRatings: number } | null {
    const record = this.asFeedbackRecord(response);
    const nestedResult = this.asFeedbackRecord(record['result']);
    const summary = this.asFeedbackRecord(nestedResult['summary'] ?? record['summary']);
    const averageRaw = summary['averageRating'] ?? summary['average_rating'];
    const totalRaw = summary['totalRatings'] ?? summary['total_ratings'] ?? summary['totalCount'];
    const averageRating = Number(averageRaw);
    const totalRatings = Number(totalRaw);
    if (!Number.isFinite(averageRating) && !Number.isFinite(totalRatings)) {
      return null;
    }
    return {
      averageRating: Number.isFinite(averageRating) ? averageRating : null,
      totalRatings: Number.isFinite(totalRatings) ? totalRatings : 0,
    };
  }

  private summarizeDatasetReviews(
    rows: DatasetReviewItem[],
  ): { averageRating: number | null; totalRatings: number } | null {
    if (!rows.length) {
      return null;
    }
    const rated = rows.filter((row) => row.ratingValue > 0);
    const total = rated.reduce((sum, row) => sum + row.ratingValue, 0);
    return {
      averageRating: rated.length ? total / rated.length : null,
      totalRatings: rated.length,
    };
  }

  private readFeedbackCommentFromSubdata(value: unknown): string {
    if (typeof value === 'string') {
      try {
        return this.readFeedbackCommentFromSubdata(JSON.parse(value) as unknown);
      } catch {
        return value.trim();
      }
    }
    const record = this.asFeedbackRecord(value);
    return this.readFeedbackString(record, ['comment', 'feedback', 'text', 'message']);
  }

  private readFeedbackString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private asFeedbackRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }


  /** Normalize concepts from item payload (strings, objects with label/name, or comma-separated string). */
  private extractConceptLabels(datasetData: any): string[] {
    const raw = datasetData?.concepts ?? datasetData?.concept;
    if (raw == null || raw === '') {
      return [];
    }
    if (typeof raw === 'string') {
      return [
        ...new Set(
          raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ];
    }
    if (!Array.isArray(raw)) {
      return [];
    }
    const labels: string[] = [];
    for (const item of raw) {
      if (item == null) continue;
      if (typeof item === 'string') {
        const t = item.trim();
        if (t) labels.push(t);
      } else if (typeof item === 'object') {
        const label = item.label ?? item.name ?? item.title ?? item.value;
        if (label != null && String(label).trim() !== '') {
          labels.push(String(label).trim());
        }
      }
    }
    return [...new Set(labels)];
  }

  async loadDatasetDetails(): Promise<void> {
    if (!this.datasetId) {
      console.error(
        'Cannot load dataset details: datasetId is null or undefined',
      );
      return;
    }

    try {
      console.log('Loading dataset details for ID:', this.datasetId);

      // Call API: <basepathurl><apiCatPat>/item?id=<id of the dataset>
      const response = await this.catalogueApiService.getItemById(
        this.datasetId,
        {
          auditEnabled: this.itemAuditEnabled,
        },
      );
      console.log('API Response received:', response);

      // Handle response structure - could be direct object or nested in result
      let datasetData = response;
      if (response?.result) {
        datasetData = Array.isArray(response.result)
          ? response.result[0]
          : response.result;
      } else if (response?.data) {
        datasetData = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
      } else if (
        response?.items &&
        Array.isArray(response.items) &&
        response.items.length > 0
      ) {
        datasetData = response.items[0];
      } else if (Array.isArray(response) && response.length > 0) {
        datasetData = response[0];
      }

      console.log('Extracted datasetData:', datasetData);
      console.log('DatasetData type:', typeof datasetData);
      console.log(
        'DatasetData is object:',
        datasetData && typeof datasetData === 'object',
      );

      if (!datasetData) {
        console.error(
          'No dataset data found in response. Response structure:',
          response,
        );
        this.datasetConcepts = [];
        // Update header to show error state
        this.headerConfig.description =
          'Failed to load dataset information. Please try again later.';
        this.cdr.detectChanges();
        return;
      }

      console.log('Dataset data loaded:', datasetData);
      console.log('Dataset data keys:', Object.keys(datasetData));

      // Extract short description (use shortDescription if available, otherwise truncate description)
      let shortDescription =
        datasetData.shortDescription || datasetData.summary || '';
      if (!shortDescription && datasetData.description) {
        // Truncate description to first 150 characters if no short description available
        shortDescription =
          datasetData.description.length > 150
            ? datasetData.description.substring(0, 150).trim() + '...'
            : datasetData.description;
      }

      // Extract organization name for subtitle
      const organizationName =
        datasetData.provider ||
        datasetData.organisation ||
        datasetData.organization ||
        '';

      // Update header config with actual dataset data
      const datasetTitle =
        datasetData.label || datasetData.name || 'Dataset Details';
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Datasets', route: '/datasets' },
          { label: datasetTitle },
        ],
        title: datasetTitle,
        description: shortDescription || 'Loading dataset information...',
        assetId: this.datasetId || datasetData.id || '',
      };

      // Store organization name for subtitle display
      if (organizationName && this.datasetDetails) {
        this.datasetDetails.uploadedBy = organizationName;
      }

      // Extract description
      this.datasetDescription =
        datasetData.description ||
        datasetData.shortDescription ||
        datasetData.summary ||
        '';

      // Extract tags
      this.datasetTags = [];
      if (datasetData.tags && Array.isArray(datasetData.tags)) {
        this.datasetTags = datasetData.tags;
      } else if (datasetData.tag && Array.isArray(datasetData.tag)) {
        this.datasetTags = datasetData.tag;
      }

      console.log('Dataset description:', this.datasetDescription);
      console.log('Dataset tags:', this.datasetTags);

      this.datasetConcepts = this.extractConceptLabels(datasetData);

      // Extract dataset details
      // Extract organisation type from organizationType field
      const organisationType =
        datasetData.organizationType ||
        datasetData.organisationType ||
        'Public';

      // Extract industry from industry field
      const industry = datasetData.industry || '';

      // Extract year range from temporalCoverage or yearRange field
      let yearRange = '';
      if (datasetData.temporalCoverage) {
        if (typeof datasetData.temporalCoverage === 'string') {
          yearRange = datasetData.temporalCoverage;
        } else if (
          datasetData.temporalCoverage.start &&
          datasetData.temporalCoverage.end
        ) {
          const startYear = new Date(
            datasetData.temporalCoverage.start,
          ).getFullYear();
          const endYear = new Date(
            datasetData.temporalCoverage.end,
          ).getFullYear();
          yearRange = `${startYear}-${endYear}`;
        }
      } else if (datasetData.yearRange) {
        yearRange = datasetData.yearRange;
      }

      const geoCoverage = String(
        datasetData.geoCoverage ??
          datasetData.geo_coverage ??
          datasetData.spatialCoverage ??
          datasetData.spatial_coverage ??
          '',
      ).trim();

      const rawLicense = datasetData.license || datasetData.licence || '';
      this.licenseUsesOpenMarker = rawLicense.startsWith('__');

      // Populate datasetDetails - ensure at least some data is set even if fields are missing
      this.datasetDetails = {
        uploadedBy:
          datasetData.provider ||
          datasetData.organisation ||
          datasetData.organization ||
          '',
        organisationType: organisationType || '',
        industry: industry || '',
        organizationName: organizationName || '',
        yearRange: yearRange || '',
        geoCoverage: geoCoverage || '',
        datasetPermission:
          this.helperService.extractAccessBadge(datasetData) || 'OPEN',
        lastUpdated:
          this.formatService.formatDate(
            datasetData.itemCreatedAt ||
              datasetData.createdAt ||
              datasetData.lastUpdated,
          ) || '',
        fileFormat: datasetData.fileFormat || datasetData.format || '',
        license: this.formatService.resolveCatalogueLicenseDisplay(
          this.stripLicenseMarker(rawLicense),
          datasetData.otherLicense || datasetData.other_license,
        ),
        uploadFrequency:
          datasetData.uploadFrequency ||
          datasetData.updateFrequency ||
          'One-time',
        dataReadiness:
          datasetData.dataReadiness ||
          datasetData.quality ||
          datasetData.dataQuality ||
          0,
        verifiedBy:
          datasetData.verifiedBy ||
          datasetData.provider ||
          datasetData.organisation ||
          datasetData.organization ||
          '',
        resourceType:
          datasetData.resourceType || datasetData.resource_type || '',
        accessPolicy:
          datasetData.accessPolicy || datasetData.access_policy || '',
        publishStatus:
          datasetData.publishStatus || datasetData.publish_status || '',
      };

      // Ensure datasetDetails has at least one property to pass hasDatasetDetails check
      if (Object.keys(this.datasetDetails).length === 0) {
        // Fallback: set at least one property to ensure content displays
        this.datasetDetails.uploadedBy =
          datasetData.label || datasetData.name || 'Unknown';
      }

      // Dataset card details: updatedAt in DD-MMM-YYYY HH:mm format (ensure short month, 24h time)
      const badge = this.helperService.extractAccessBadge(datasetData);
      const lastUpdatedRaw = datasetData.lastUpdated;
      const updatedAtFormatted = lastUpdatedRaw
        ? this.formatUpdatedAtForCard(lastUpdatedRaw)
        : '';
      this.datasetCardDetails = {
        badge: badge || 'Public',
        createdAt:
          this.formatService.formatDateInIST(
            datasetData.itemCreatedAt || datasetData.createdAt,
          ) || '',
        updatedAt: updatedAtFormatted || '',
        fileFormat: datasetData.fileFormat || datasetData.format || '',
        uploadFrequency:
          datasetData.uploadFrequency ||
          datasetData.updateFrequency ||
          'One-time',
        verifiedBy:
          datasetData.provider ||
          datasetData.organisation ||
          datasetData.organization ||
          '',
        dataReadiness: datasetData.dataReadiness || '',
      };

      // Additional Information
      this.additionalInformation =
        datasetData.additionalInfo ||
        datasetData.notes ||
        datasetData.description ||
        '';

      // Store full dataset data for accessing resourceServer
      this.fullDatasetData = datasetData;
      this.refreshApiExplorerContext();

      // Populate Activity Overview from Item API metrics (use API values as-is; do not add 1)
      const metrics = datasetData.metrics || datasetData;
      this.activityMetrics.likes =
        metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads =
        metrics.downloads ??
        metrics.downloadCount ??
        metrics.download_count ??
        0;
      this.activityMetrics.views =
        metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0;
      this.activityMetrics.bookmarks =
        metrics.bookmarks ??
        metrics.bookmarkCount ??
        metrics.bookmark_count ??
        0;

      // If current tab is Details but dataset has no FILE (View file), switch to dataset tab
      // Details tab is always visible; no redirect away from it based on FILE resource.

      // Log publishStatus for debugging
      console.log('Dataset publishStatus:', {
        publishStatus: datasetData.publishStatus || datasetData.publish_status,
        fullDatasetDataPublishStatus: this.fullDatasetData?.publishStatus,
        datasetDetailsPublishStatus: this.datasetDetails?.['publishStatus'],
      });

      // Load sample data only when item API returns dataSample / data_sample
      const rawSample = datasetData.dataSample ?? datasetData.data_sample;
      if (rawSample) {
        if (typeof rawSample === 'string') {
          try {
            this.sampleData = JSON.parse(rawSample);
          } catch (error) {
            console.warn(
              'Failed to parse dataSample as JSON, using as-is:',
              error,
            );
            this.sampleData = rawSample;
          }
        } else if (typeof rawSample === 'object' && rawSample !== null) {
          this.sampleData = rawSample;
        }
        console.log('Sample data loaded from API:', this.sampleData);
      } else {
        this.sampleData = null;
      }

      const rawDescriptor =
        datasetData.dataDescriptor ?? datasetData.data_descriptor;
      this.dataDescriptors = parseCatalogueDataDescriptors(rawDescriptor);
      console.log(
        'Data descriptors loaded from API:',
        this.dataDescriptors.length,
      );

      console.log('Dataset details populated:', this.datasetDetails);
      console.log('hasDatasetDetails:', this.hasDatasetDetails);

      // Check bookmark, like, and approved ratings if the user is logged in
      if (this.isLoggedIn) {
        await Promise.all([
          this.checkBookmarkAndLikeStatus(),
          this.loadUserFeedback(),
        ]);
      }

      // Trigger change detection to update the view
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading dataset details:', error);
      this.datasetConcepts = [];
      // Update header to show error state
      this.headerConfig = {
        ...this.headerConfig,
        description:
          'Failed to load dataset information. Please try again later.',
      };
      // Keep default/fallback values on error
      this.cdr.detectChanges();
    }

    // Call hasAccess API to check user access after dataset details are loaded
    // Don't await this - it shouldn't block content display
    this.checkUserAccess().catch((error) => {
      console.error('Error checking user access:', error);
      // Don't block content display if access check fails
    });

    // Load similar datasets based on tags
    // Don't await this - it shouldn't block content display
    this.loadSimilarDatasets().catch((error) => {
      console.error('Error loading similar datasets:', error);
      // Don't block content display if similar datasets fail to load
    });

    // If user is on Details, Visualisation or SQL Playground tab (e.g. landed with ?tab=details), load that tab's data now that datasetId is set
    if (
      this.activeTab === 'details' ||
      this.activeTab === 'visualisation' ||
      this.activeTab === 'sql-playground'
    ) {
      this.loadDetailsTabData();
    }
  }

  async checkUserAccess(): Promise<void> {
    console.log('checkUserAccess called, datasetId:', this.datasetId);
    if (!this.datasetId) {
      console.warn('Cannot check access: datasetId is null or undefined');
      return;
    }

    const pendingUrn = DatasetDetailComponent.ACCESS_PENDING_URN;
    const noAccessUrn = DatasetDetailComponent.ACCESS_NO_ACCESS_URN;
    const successUrn = DatasetDetailComponent.ACCESS_SUCCESS_URN;

    // Don't call has_access API when accessPolicy is OPEN
    const accessPolicy = (
      this.datasetDetails?.accessPolicy ??
      this.datasetDetails?.['access_policy'] ??
      ''
    )
      .toString()
      .toUpperCase();
    if (accessPolicy === 'OPEN') {
      this.accessRequestPending = false;
      this.accessStatus = { type: successUrn };
      console.log('Access policy is OPEN, skipping has_access API');
      return;
    }

    // Check if user is logged in
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      console.log('User logged in status:', this.isLoggedIn);

      if (!this.isLoggedIn) {
        // Don't call API if user is not logged in
        console.log('User is not logged in, skipping access check');
        return;
      }

      const payload = {
        itemId: this.datasetId,
      };
      console.log('Calling hasAccess API with payload:', payload);
      const seq = ++this.accessCheckSeq;

      const applyAccessStatus = (next: any): void => {
        if (seq !== this.accessCheckSeq) return;
        if (!next || typeof next !== 'object') return;
        this.applyPendingAwareAccessStatus(next);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      };

      try {
        const response = await this.aclApdApiService.hasAccess(payload);
        console.log('Has access response:', response);

        // Normalize the 200 OK response.
        // A 200 from has_access always means the user has access — unless the body
        // explicitly says pending or no-access. The normalizer's urn: catch-all can
        // misclassify the dataset's own type URN in the response body as an access
        // status, so we override anything that isn't explicitly pending/no-access.
        const normalized = normalizeAclAccessStatus(response);
        const t = normalized.type ?? '';
        const resolvedStatus =
          t === pendingUrn || t === noAccessUrn
            ? normalized
            : { type: successUrn };
        applyAccessStatus(resolvedStatus);
      } catch (err: unknown) {
        console.log('Has access API error (may contain status info):', err);

        // normalizeAclAccessStatus handles HttpErrorResponse.error extraction and JSON string parsing.
        const normalized = normalizeAclAccessStatus(err);
        if (Object.keys(normalized).length > 0) {
          applyAccessStatus(normalized);
          // If access not yet granted, redirect away from resource-server tabs.
          if (
            normalized.type !== successUrn &&
            (this.activeTab === 'details' ||
              this.activeTab === 'visualisation' ||
              this.activeTab === 'sql-playground')
          ) {
            this.activeTab = 'dataset';
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { tab: 'dataset' },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        } else if (!this.isAccessRequestPendingState()) {
          applyAccessStatus({});
        }
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      // Preserve optimistic pending state â€” don't overwrite it on a transient error.
      if (!this.isAccessRequestPendingState()) {
        this.accessStatus = {};
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    }
  }

  private isAccessRequestPendingState(): boolean {
    return (
      this.accessRequestPending ||
      this.accessStatus?.type === DatasetDetailComponent.ACCESS_PENDING_URN
    );
  }

  private markAccessRequestPending(): void {
    this.accessRequestPending = true;
    this.accessStatus = { type: DatasetDetailComponent.ACCESS_PENDING_URN };
  }

  /** Apply has_access result without clearing a just-submitted pending request. */
  private applyPendingAwareAccessStatus(next: { type?: string }): void {
    const pendingUrn = DatasetDetailComponent.ACCESS_PENDING_URN;
    const successUrn = DatasetDetailComponent.ACCESS_SUCCESS_URN;
    const noAccessUrn = DatasetDetailComponent.ACCESS_NO_ACCESS_URN;
    const nextType = (next?.type ?? '').trim();

    if (nextType === successUrn) {
      this.accessRequestPending = false;
      this.accessStatus = next;
      return;
    }

    if (nextType === pendingUrn) {
      this.accessRequestPending = true;
      this.accessStatus = next;
      return;
    }

    if (this.isAccessRequestPendingState()) {
      if (!nextType || nextType === noAccessUrn) {
        return;
      }
    }

    this.accessStatus = next;
    if (nextType && nextType !== pendingUrn) {
      this.accessRequestPending = false;
    }
  }

  async onGetData(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.datasetId) {
      console.error('Cannot get data: datasetId is null or undefined');
      return;
    }
    if (
      this.shouldDisableRsActionsUntilAccessOk ||
      this.shouldDisableGetDataForFileServerFlow
    )
      return;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.googleAnalytics.trackDatasetGetDataClick(
      this.headerConfig.title ?? '',
      this.datasetId ?? '',
    );
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: ResourceServer, t: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(t);
    const hasQ = (s: ResourceServer, t: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((q) =>
        String(q).toUpperCase().includes(t),
      );
    const hasOgcStac = list.some(
      (s) => nameMatches(s, 'OGC') && hasQ(s, 'STAC'),
    );
    const hasOgcFeature = list.some(
      (s) => nameMatches(s, 'OGC') && hasQ(s, 'FEATURE'),
    );
    const isNgsiLdAttr =
      this.hasQueryType('ATTR') &&
      !hasOgcStac &&
      !hasOgcFeature &&
      !this.hasGatewayResourceServer();
    if (hasOgcStac || hasOgcFeature) {
      this.openServerSpecificGetDataModal();
    } else if (this.isFileServerDatabankGetDataFlow) {
      this.showGetDataModal = true;
      this.getDataMode = 'file';
      if (this.getDataPdfUrl?.startsWith('blob:'))
        URL.revokeObjectURL(this.getDataPdfUrl);
      this.revokeGetDataFileDownloadUrl();
      this.getDataPdfUrl = null;
      this.getDataDocxHtml = '';
      this.getDataFileName = '';
      this.getDataNoFilesAvailable = false;
      this.getDataLoading = true;
      this.cdr.markForCheck();
      try {
        const apiFiles = await this.resolveApiFilesForGetData();
        if (this.collectSortedFileKeysForGetData(apiFiles).length === 0) {
          this.getDataNoFilesAvailable = true;
          return;
        }
        await this.loadGetDataFile();
      } finally {
        this.getDataLoading = false;
        this.cdr.markForCheck();
      }
    } else if (isNgsiLdAttr) {
      this.showGetDataModal = true;
      this.getDataMode = 'entity';
      this.getDataEntries = [];
      this.getDataCurrentPage = 1;
      this.getDataTotalPages = 1;
      await this.loadGetData(1);
    } else {
      this.openServerSpecificGetDataModal();
    }
  }

  /** Decide which Get Data modal to show based on resourceServer configuration (MahaAgX-style). */
  private openServerSpecificGetDataModal(): void {
    const list = this.resourceServersList as ResourceServer[];

    const nameMatches = (server: ResourceServer, target: string): boolean => {
      const name = (server?.name ?? '').toString().toUpperCase();
      return name === target || name.includes(target);
    };

    const hasAccessType = (server: ResourceServer, type: string): boolean => {
      if (!Array.isArray(server.accessTypes)) return false;
      return server.accessTypes.some((t) => String(t).toUpperCase() === type);
    };

    const hasQueryType = (server: ResourceServer, type: string): boolean => {
      if (!Array.isArray(server.queryTypes)) return false;
      const target = type.toUpperCase();
      return server.queryTypes.some((t) =>
        String(t).toUpperCase().includes(target),
      );
    };

    // 1. OGC with queryTypes STAC â†’ OGC STAC Data popup
    const ogcStacServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'),
    );
    if (ogcStacServer) {
      this.featureDataMode = {
        type: 'OGC_STAC',
        accessTypes: ogcStacServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 2. OGC with queryTypes FEATURE â†’ OGC Feature Data popup
    const ogcFeatureServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE'),
    );
    if (ogcFeatureServer) {
      this.featureDataMode = {
        type: 'OGC_FEATURE',
        accessTypes: ogcFeatureServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 3. GATEWAY
    const gatewayServer = list.find((s) => nameMatches(s, 'GATEWAY'));
    if (gatewayServer) {
      const accessTypes = Array.isArray(gatewayServer.accessTypes)
        ? gatewayServer.accessTypes
        : [];
      const hasAttr = accessTypes.some(
        (t) => String(t).toUpperCase() === 'ATTR',
      );
      const hasTemporal = accessTypes.some(
        (t) => String(t).toUpperCase() === 'TEMPORAL',
      );
      const dataModel: 'model1' | 'model2' =
        hasAttr && hasTemporal ? 'model2' : 'model1';

      this.featureDataMode = {
        type: 'GATEWAY',
        dataModel,
        accessTypes,
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 4. Fallback â€“ use default GATEWAY-style
    this.featureDataMode = {
      type: 'DEFAULT',
      dataModel: 'model1',
      accessTypes: [],
    };
    this.isFeatureDataOpen = true;
    this.cdr.markForCheck();
  }

  onCloseFeatureData(): void {
    this.isFeatureDataOpen = false;
    this.cdr.markForCheck();
  }

  get featureDataResourceItem(): {
    id: string;
    label: string;
    ogcServerBaseUrl?: string;
    apiServerBaseUrl?: string;
    searchValues?: string[];
    placeholderValues?: Record<string, string>;
  } {
    const meta = this.datasetDetails;
    const id =
      this.datasetId ||
      (this.fullDatasetData?.id ? String(this.fullDatasetData.id) : '') ||
      '';
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: any, n: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(n);
    const hasQueryType = (s: any, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) =>
        String(t).toUpperCase().includes(String(type).toUpperCase()),
      );
    const ogcFeature = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE'),
    );
    const ogcServerBaseUrl = (ogcFeature as any)?.url
      ? String((ogcFeature as any).url).replace(/\/$/, '')
      : undefined;
    const gateway = list.find((s) => nameMatches(s, 'GATEWAY'));
    const apiServerBaseUrl = this.resolveGatewayApiBaseUrl(gateway);
    const searchValues = this.resolveGatewaySearchValues(gateway);
    const placeholderValues =
      this.extractGatewayPlaceholderExamples(searchValues);
    return {
      id,
      label: meta?.organizationName
        ? `${meta.organizationName} Dataset`
        : meta?.uploadedBy
          ? `${meta.uploadedBy} Dataset`
          : 'Dataset',
      ogcServerBaseUrl,
      apiServerBaseUrl,
      ...(searchValues.length ? { searchValues } : {}),
      ...(Object.keys(placeholderValues).length ? { placeholderValues } : {}),
    };
  }

  /** Gateway popup fields: resourceServer.searchValues, else item dataQueryParams. */
  private resolveGatewaySearchValues(
    gateway?: ResourceServer | null,
  ): string[] {
    const fromServer = this.normalizeStringList(gateway?.searchValues);
    if (fromServer.length) return fromServer;
    return this.normalizeStringList(this.fullDatasetData?.dataQueryParams);
  }

  private normalizeStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of value) {
      const key = String(item ?? '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }

  /** Example values for gateway input placeholders, taken from item sample data. */
  private extractGatewayPlaceholderExamples(
    keys: string[],
  ): Record<string, string> {
    const out: Record<string, string> = {};
    if (!keys.length) return out;

    const sample =
      this.sampleData ??
      this.fullDatasetData?.dataSample ??
      this.fullDatasetData?.data_sample;
    if (sample == null) return out;

    const aliasesFor = (key: string): string[] => {
      const lower = key.toLowerCase();
      if (lower === 'latitude' || lower === 'lat') return ['latitude', 'lat'];
      if (
        lower === 'longitude' ||
        lower === 'long' ||
        lower === 'lng' ||
        lower === 'lon'
      ) {
        return ['longitude', 'long', 'lng', 'lon'];
      }
      return [key];
    };

    for (const key of keys) {
      const found = this.findSamplePrimitiveByAliases(sample, aliasesFor(key));
      if (found != null) out[key] = found;
    }

    const needsLat = keys.some((k) => {
      const lower = k.toLowerCase();
      return (lower === 'latitude' || lower === 'lat') && !out[k];
    });
    const needsLng = keys.some((k) => {
      const lower = k.toLowerCase();
      return (
        (lower === 'longitude' ||
          lower === 'long' ||
          lower === 'lng' ||
          lower === 'lon') &&
        !out[k]
      );
    });
    if (needsLat || needsLng) {
      const pair = this.findFirstGeoJsonLonLat(sample);
      if (pair) {
        for (const key of keys) {
          if (out[key]) continue;
          const lower = key.toLowerCase();
          if (lower === 'latitude' || lower === 'lat') out[key] = pair.lat;
          if (
            lower === 'longitude' ||
            lower === 'long' ||
            lower === 'lng' ||
            lower === 'lon'
          ) {
            out[key] = pair.lon;
          }
        }
      }
    }
    return out;
  }

  private findSamplePrimitiveByAliases(
    node: unknown,
    aliases: string[],
    depth = 0,
  ): string | null {
    if (node == null || depth > 12) return null;
    const wanted = new Set(aliases.map((a) => a.toLowerCase()));
    if (Array.isArray(node)) {
      for (const item of node) {
        const found = this.findSamplePrimitiveByAliases(
          item,
          aliases,
          depth + 1,
        );
        if (found != null) return found;
      }
      return null;
    }
    if (typeof node !== 'object') return null;
    const obj = node as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      if (!wanted.has(key.toLowerCase())) continue;
      const text = this.sampleValueToPlaceholder(obj[key]);
      if (text != null) return text;
    }
    for (const value of Object.values(obj)) {
      const found = this.findSamplePrimitiveByAliases(
        value,
        aliases,
        depth + 1,
      );
      if (found != null) return found;
    }
    return null;
  }

  private sampleValueToPlaceholder(raw: unknown): string | null {
    let value = raw;
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>;
      value = nested['value'] ?? value;
    }
    if (typeof value === 'number' && Number.isFinite(value))
      return String(value);
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
    return null;
  }

  /** GeoJSON positions are [longitude, latitude]. */
  private findFirstGeoJsonLonLat(
    node: unknown,
    depth = 0,
  ): { lon: string; lat: string } | null {
    if (node == null || depth > 16) return null;
    if (Array.isArray(node)) {
      if (
        node.length >= 2 &&
        typeof node[0] === 'number' &&
        typeof node[1] === 'number' &&
        Number.isFinite(node[0]) &&
        Number.isFinite(node[1])
      ) {
        const lon = node[0];
        const lat = node[1];
        if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
          return { lon: String(lon), lat: String(lat) };
        }
      }
      for (const item of node) {
        const found = this.findFirstGeoJsonLonLat(item, depth + 1);
        if (found) return found;
      }
      return null;
    }
    if (typeof node === 'object') {
      for (const value of Object.values(node as Record<string, unknown>)) {
        const found = this.findFirstGeoJsonLonLat(value, depth + 1);
        if (found) return found;
      }
    }
    return null;
  }

  /** Resolve `.../rsp` base for Gateway Get Data (env gatewayApiPath, then catalogue url). */
  private resolveGatewayApiBaseUrl(
    gateway?: ResourceServer | null,
  ): string | undefined {
    const envGateway = (
      (environment as { gatewayApiPath?: string }).gatewayApiPath ?? ''
    ).replace(/\/+$/, '');
    const catalogueUrl = gateway?.url
      ? String(gateway.url).replace(/\/+$/, '')
      : '';
    const candidate = envGateway || catalogueUrl;
    if (!candidate) return undefined;
    if (/\/ngsi-ld\/v1\/entities$/i.test(candidate)) {
      return candidate.replace(/\/ngsi-ld\/v1\/entities$/i, '');
    }
    if (/\/rsp$/i.test(candidate)) return candidate;
    if (/\/dataplane$/i.test(candidate)) return `${candidate}/rsp`;
    // Bare RS hosts (e.g. https://v2.dev.rs.iudx.io) need /rsp
    return `${candidate}/rsp`;
  }

  get stacResourceItem(): {
    id: string;
    label: string;
    fileFormat: string;
    stacServerBaseUrl?: string;
  } {
    const meta = this.datasetDetails;
    const id =
      this.datasetId ||
      (this.fullDatasetData?.id ? String(this.fullDatasetData.id) : '') ||
      '';
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: any, n: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(n);
    const hasQueryType = (s: any, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) =>
        String(t).toUpperCase().includes(String(type).toUpperCase()),
      );
    const ogcStac = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'),
    );
    const stacServerBaseUrl = (ogcStac as any)?.url
      ? String((ogcStac as any).url).replace(/\/$/, '')
      : undefined;
    return {
      id,
      label: meta?.organizationName
        ? `${meta.organizationName} Dataset`
        : meta?.uploadedBy
          ? `${meta.uploadedBy} Dataset`
          : 'Dataset',
      fileFormat: meta?.fileFormat ?? '',
      stacServerBaseUrl,
    };
  }

  /**
   * FILE-only Get Data: prefer per-file download via file API; fall back to databank GET download (ZIP/presigned).
   */
  async loadGetDataFile(): Promise<void> {
    if (!this.datasetId) return;
    if (!this.isFileServerDatabankGetDataFlow) {
      this.getDataLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.getDataLoading = true;
    if (this.getDataPdfUrl?.startsWith('blob:'))
      URL.revokeObjectURL(this.getDataPdfUrl);
    this.revokeGetDataFileDownloadUrl();
    this.getDataPdfUrl = null;
    this.getDataDocxHtml = '';
    this.getDataFileKeys = [];
    this.getDataSelectedFileKey = null;
    this.getDataZipContents = null;
    this.getDataFileSwitchLoading = false;
    try {
      const viaFiles = await this.tryLoadGetDataFileViaFilesDownload();
      if (viaFiles) return;

      const {
        blob: initialBlob,
        filename: headerFilename,
        contentType: headerCt,
      } = await this.fetchDatabankDownloadBlobForGetData(this.datasetId);

      let effectiveBlob = initialBlob;
      let filename = (headerFilename || '').trim();
      const headerContentType = (headerCt || '').toLowerCase();

      const peekLen = Math.min(256, initialBlob.size || 0);
      const peek =
        peekLen > 0
          ? (await initialBlob.slice(0, peekLen).text()).trimStart()
          : '';
      const responseLooksJson =
        headerContentType.includes('application/json') ||
        headerContentType.includes('text/json') ||
        (initialBlob.size < 2_000_000 && peek.startsWith('{'));

      if (responseLooksJson && peek.startsWith('{')) {
        const text = await initialBlob.text();
        let dl: string | undefined;
        try {
          const j = JSON.parse(text) as {
            data?: {
              downloadUrl?: string;
              presignedUrl?: string;
              url?: string;
            };
            downloadUrl?: string;
          };
          dl =
            j?.data?.downloadUrl ??
            j?.data?.presignedUrl ??
            j?.data?.url ??
            j?.downloadUrl;
        } catch {
          this.toasterService.error(
            'Unexpected response from databank download.',
          );
          return;
        }
        if (!dl || typeof dl !== 'string') {
          this.toasterService.error('No download URL in databank response.');
          return;
        }
        effectiveBlob = await this.fetchBlobWithoutAuthInterceptor(
          this.resolveDatabankAssetDownloadUrl(dl),
        );
        if (!filename) filename = this.filenameFromUrl(dl);
      }

      const typeHint = (effectiveBlob.type || headerCt || '').toLowerCase();
      await this.processGetDataFileBlob(effectiveBlob, filename, typeHint);
    } catch (err) {
      console.error('Error loading file for Get Data:', err);
      this.toasterService.error('Failed to load file from file server.');
    } finally {
      this.getDataLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Called when Get Data modal visibility changes; reset FILE mode URLs. */
  onGetDataModalVisibleChange(visible: boolean): void {
    if (!visible) {
      if (this.getDataPdfUrl?.startsWith('blob:'))
        URL.revokeObjectURL(this.getDataPdfUrl);
      this.getDataPdfUrl = null;
      this.getDataDocxHtml = '';
      this.revokeGetDataFileDownloadUrl();
      this.getDataFileName = '';
      this.getDataFileKeys = [];
      this.getDataSelectedFileKey = null;
      this.getDataZipContents = null;
      this.getDataFileSwitchLoading = false;
      this.getDataNoFilesAvailable = false;
    }
  }

  /** Short label for the file rail (basename / path tail). */
  getDataFileDisplayLabel(pathOrKey: string): string {
    const s = (pathOrKey || '').replace(/\\/g, '/');
    const i = s.lastIndexOf('/');
    return i >= 0 ? s.slice(i + 1) : s;
  }

  get getDataModalToolbarLabel(): string {
    if (this.getDataDocxHtml && !this.getDataPdfUrl) return 'Document preview';
    return 'PDF preview';
  }

  /**
   * User picked another file in the Get Data modal (files API multi-key or multi-entry ZIP).
   */
  async onGetDataFileKeySelect(key: string): Promise<void> {
    if (!key) return;
    const hasPreview = !!(
      this.getDataPdfUrl ||
      (this.getDataDocxHtml && this.getDataDocxHtml.trim()) ||
      this.getDataFileDownloadUrl
    );
    if (key === this.getDataSelectedFileKey && hasPreview) return;

    const prevPdfBlobUrl = this.getDataPdfUrl;

    this.getDataFileSwitchLoading = true;
    this.cdr.markForCheck();
    try {
      if (this.getDataZipContents && this.getDataZipContents[key]) {
        this.getDataSelectedFileKey = key;
        await this.renderGetDataZipEntry(this.getDataZipContents, key);
        if (
          prevPdfBlobUrl?.startsWith('blob:') &&
          prevPdfBlobUrl !== this.getDataPdfUrl
        ) {
          URL.revokeObjectURL(prevPdfBlobUrl);
        }
        return;
      }
      if (!this.datasetId) return;
      const blob = await this.fileApiService.getFileBlob(this.datasetId, {
        key,
        presigned: false,
      });
      this.getDataSelectedFileKey = key;
      await this.processGetDataFileBlob(
        blob,
        key,
        (blob.type || '').toLowerCase(),
      );
    } catch (err) {
      console.error('Get Data: failed to load selected file', err);
      this.toasterService.error('Failed to load the selected file.');
    } finally {
      this.getDataFileSwitchLoading = false;
      this.cdr.markForCheck();
    }
  }

  private collectSortedFileKeysForGetData(apiFiles: any[]): string[] {
    const files = (apiFiles || []).filter((f: any) => f && f.isFile !== false);
    const keys = files
      .map((f: any) => f.key)
      .filter((k: any) => k != null && String(k).trim() !== '')
      .map((k: any) => String(k));
    const uniq = [...new Set(keys)];
    uniq.sort((a, b) => a.localeCompare(b));
    return uniq;
  }

  private pickPrimaryFileKeyForGetData(apiFiles: any[]): string | null {
    const files = (apiFiles || []).filter((f: any) => f && f.isFile !== false);
    if (files.length === 0) return null;
    const ff = (this.datasetDetails?.fileFormat ?? '')
      .toString()
      .toLowerCase()
      .replace(/^\./, '');
    if (ff) {
      const match = files.find((f: any) => {
        const k = String(f.key ?? '').toLowerCase();
        return k.endsWith(`.${ff}`) || k === ff;
      });
      if (match?.key) return String(match.key);
    }
    const sorted = [...files].sort((a: any, b: any) =>
      String(a.key ?? '').localeCompare(String(b.key ?? '')),
    );
    return sorted[0]?.key != null ? String(sorted[0].key) : null;
  }

  /** Files list for Get Data (version files from Details tab, or live files API). */
  private async resolveApiFilesForGetData(): Promise<any[]> {
    if (!this.datasetId) return [];
    if (this.versionFiles?.length) {
      return this.versionFiles.map((f) => ({ key: f.name, isFile: true }));
    }
    try {
      const res = await this.fileApiService.getFilesForPreview(this.datasetId);
      return this.extractFilesFromFilesResponse(res?.data);
    } catch {
      return [];
    }
  }

  private async tryLoadGetDataFileViaFilesDownload(): Promise<boolean> {
    if (!this.datasetId) return false;
    const apiFiles = await this.resolveApiFilesForGetData();
    const keys = this.collectSortedFileKeysForGetData(apiFiles);
    if (!keys.length) return false;

    this.getDataZipContents = null;
    const initialKey = this.pickPrimaryFileKeyForGetData(apiFiles) ?? keys[0];
    // Always show the left-rail list (even for a single file).
    this.getDataFileKeys = keys;
    this.getDataSelectedFileKey = initialKey;

    try {
      const blob = await this.fileApiService.getFileBlob(this.datasetId, {
        key: initialKey,
        presigned: false,
      });
      const headerCt = (blob.type || '').toLowerCase();
      await this.processGetDataFileBlob(blob, initialKey, headerCt);
      const hasPreview = !!(
        this.getDataPdfUrl ||
        (this.getDataDocxHtml && this.getDataDocxHtml.trim()) ||
        this.getDataFileDownloadUrl
      );
      return hasPreview;
    } catch (e) {
      console.warn(
        'Get Data: per-file download path failed, falling back to databank download.',
        e,
      );
      this.getDataFileKeys = [];
      this.getDataSelectedFileKey = null;
      return false;
    }
  }

  private async fetchDatabankDownloadBlobForGetData(
    databankId: string,
  ): Promise<{ blob: Blob; filename?: string; contentType?: string }> {
    const url = `${environment.baseFilePathUrl}v1/databanks/${databankId}/download`;
    const response = await firstValueFrom(
      this.http.get(url, { responseType: 'blob', observe: 'response' }),
    );
    if (!response.ok) {
      throw new Error(
        `Databank download failed with status ${response.status}`,
      );
    }
    const blob = response.body;
    if (!blob) throw new Error('No data received');
    const cd = response.headers.get('content-disposition');
    let filename: string | undefined;
    if (cd) {
      const utfMatch = /filename\*=UTF-8''([^;\s]+)/i.exec(cd);
      const asciiMatch = /filename="([^"]+)"/i.exec(cd);
      if (utfMatch) {
        try {
          filename = decodeURIComponent(utfMatch[1]);
        } catch {
          filename = utfMatch[1];
        }
      } else if (asciiMatch) {
        filename = asciiMatch[1];
      }
    }
    const contentType =
      response.headers.get('content-type')?.split(';')[0]?.trim() ||
      blob.type ||
      '';
    return { blob, filename, contentType };
  }

  private async fetchBlobWithoutAuthInterceptor(url: string): Promise<Blob> {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
    });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    return res.blob();
  }

  private filenameFromUrl(url: string): string {
    try {
      const u = new URL(url, window.location.origin);
      const last = u.pathname.split('/').pop();
      return last ? decodeURIComponent(last) : '';
    } catch {
      return '';
    }
  }

  private resolveDatabankAssetDownloadUrl(url: string): string {
    const t = (url || '').trim();
    if (!t) return t;
    if (/^https?:\/\//i.test(t)) return t;
    try {
      const base = new URL(environment.baseFilePathUrl);
      return new URL(t.startsWith('/') ? t : `/${t}`, base).href;
    } catch {
      return t;
    }
  }

  private async blobStartsWithZipMagic(blob: Blob): Promise<boolean> {
    if (!blob?.size || blob.size < 4) return false;
    const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    return head[0] === 0x50 && head[1] === 0x4b;
  }

  private async extractPdfFromZipBlob(
    zipBlob: Blob,
    depth = 0,
  ): Promise<Blob | null> {
    if (depth > 3) return null;
    const raw = new Uint8Array(await zipBlob.arrayBuffer());
    let files: Record<string, Uint8Array>;
    try {
      files = unzipSync(raw);
    } catch (e) {
      console.warn('Get Data: fflate unzipSync failed', e);
      return null;
    }
    const entries = Object.entries(files).filter(
      ([path, data]) =>
        path && !path.endsWith('/') && data != null && data.byteLength > 0,
    );
    if (entries.length === 0) return null;

    const withPdfExt = entries.filter(([p]) =>
      p.toLowerCase().endsWith('.pdf'),
    );
    if (withPdfExt.length > 0) {
      withPdfExt.sort(([a], [b]) => a.localeCompare(b));
      return new Blob([new Uint8Array(withPdfExt[0][1])], {
        type: 'application/pdf',
      });
    }
    for (const [, data] of entries) {
      if (data.byteLength < 5) continue;
      const sig = String.fromCharCode(
        data[0],
        data[1],
        data[2],
        data[3],
        data[4],
      );
      if (sig === '%PDF-')
        return new Blob([new Uint8Array(data)], { type: 'application/pdf' });
    }
    const innerZips = entries.filter(([p]) => p.toLowerCase().endsWith('.zip'));
    innerZips.sort(([a], [b]) => a.localeCompare(b));
    for (const [, zipBytes] of innerZips) {
      const innerBlob = new Blob([new Uint8Array(zipBytes)], {
        type: 'application/zip',
      });
      const nested = await this.extractPdfFromZipBlob(innerBlob, depth + 1);
      if (nested) return nested;
    }
    return null;
  }

  private async extractDocxFromZipBlob(zipBlob: Blob): Promise<Blob | null> {
    const raw = new Uint8Array(await zipBlob.arrayBuffer());
    let files: Record<string, Uint8Array>;
    try {
      files = unzipSync(raw);
    } catch (e) {
      console.warn('Get Data: fflate unzipSync failed (docx extract)', e);
      return null;
    }
    const docxKeys = Object.keys(files).filter(
      (p) => p && !p.endsWith('/') && p.toLowerCase().endsWith('.docx'),
    );
    if (docxKeys.length === 0) return null;
    docxKeys.sort();
    const data = files[docxKeys[0]];
    if (!data?.byteLength) return null;
    return new Blob([new Uint8Array(data)], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  }

  /** Paths inside a ZIP to show in the Get Data modal (previewable first; unsupported are downloadable). */
  private sortedZipPreviewPaths(files: Record<string, Uint8Array>): string[] {
    const isPreviewable = (p: string, data?: Uint8Array): boolean => {
      const low = (p || '').toLowerCase();
      if (low.endsWith('.pdf') || low.endsWith('.docx') || low.endsWith('.doc'))
        return true;
      if (data && data.byteLength >= 5) {
        const sig = String.fromCharCode(
          data[0],
          data[1],
          data[2],
          data[3],
          data[4],
        );
        if (sig === '%PDF-') return true;
      }
      return false;
    };

    const keys = Object.keys(files).filter((p) => {
      if (!p || p.endsWith('/')) return false;
      const data = files[p];
      return !!data?.byteLength;
    });

    keys.sort((a, b) => {
      const ap = isPreviewable(a, files[a]) ? 0 : 1;
      const bp = isPreviewable(b, files[b]) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return a.localeCompare(b);
    });
    return keys;
  }

  private revokeGetDataFileDownloadUrl(): void {
    if (this.getDataFileDownloadUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.getDataFileDownloadUrl);
    }
    this.getDataFileDownloadUrl = null;
  }

  /** Offer a local blob URL so the user can download types we cannot preview (Get Data file mode). */
  private setGetDataDownloadFromBlob(blob: Blob, filenameHint?: string): void {
    this.revokeGetDataFileDownloadUrl();
    this.getDataFileDownloadUrl = URL.createObjectURL(blob);
    if (filenameHint?.trim()) this.getDataFileName = filenameHint.trim();
  }

  /**
   * Legacy Word .doc (and mammoth-readable HTML): build a PDF blob for ng2-pdf-viewer.
   */
  private async convertLegacyDocBufferToPdfBlob(
    arrayBuffer: ArrayBuffer,
  ): Promise<Blob | null> {
    let html: string | null = null;
    try {
      const r = await mammoth.convertToHtml({ arrayBuffer });
      if (r.value?.trim()) html = r.value;
    } catch {
      // Binary .doc often fails mammoth; try raw text below.
    }
    if (html) {
      const pdf = await this.renderHtmlToPdfBlobForGetData(html);
      if (pdf) return pdf;
    }
    try {
      const r = await mammoth.extractRawText({ arrayBuffer });
      if (r.value?.trim()) return await this.renderPlainTextToPdfBlob(r.value);
    } catch {
      /* ignore */
    }
    return null;
  }

  private async renderHtmlToPdfBlobForGetData(
    html: string,
  ): Promise<Blob | null> {
    const host = document.createElement('div');
    host.style.cssText =
      'position:absolute;left:-14000px;top:0;width:816px;max-width:816px;padding:28px;box-sizing:border-box;' +
      'font-size:11pt;line-height:1.45;font-family:system-ui,Segoe UI,Roboto,Helvetica,sans-serif;' +
      'color:#111827;background:#ffffff;word-wrap:break-word;';
    host.innerHTML = html;
    document.body.appendChild(host);
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        unit: 'pt',
        format: 'a4',
        orientation: 'portrait',
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 36;
      await pdf.html(host, {
        x: margin,
        y: margin,
        width: pageW - margin * 2,
        windowWidth: 900,
        autoPaging: 'text',
        html2canvas: { scale: 0.75, useCORS: true, logging: false },
      });
      return pdf.output('blob');
    } catch (e) {
      console.error('Get Data: render HTML to PDF failed', e);
      return null;
    } finally {
      host.remove();
    }
  }

  private async renderPlainTextToPdfBlob(text: string): Promise<Blob> {
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4',
      orientation: 'portrait',
    });
    const margin = 48;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxW = pageWidth - margin * 2;
    const normalized = text.replace(/\r\n/g, '\n');
    const lines = pdf.splitTextToSize(normalized, maxW);
    let y = margin;
    const lineHeight = 13;
    for (let i = 0; i < lines.length; i++) {
      if (y > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(lines[i], margin, y);
      y += lineHeight;
    }
    return pdf.output('blob');
  }

  private async renderGetDataZipEntry(
    files: Record<string, Uint8Array>,
    path: string,
  ): Promise<void> {
    // Do not clear preview or revoke blob URLs here â€” onGetDataFileKeySelect revokes the
    // previous PDF after the new preview is set so the modal keeps its height while loading.
    this.revokeGetDataFileDownloadUrl();

    const data = files[path];
    if (!data?.byteLength) return;

    const base = path.replace(/\\/g, '/').split('/').pop() || path;
    this.getDataFileName = base;

    const low = path.toLowerCase();
    const isLikelyPdf =
      low.endsWith('.pdf') ||
      (data.byteLength >= 5 &&
        String.fromCharCode(data[0], data[1], data[2], data[3], data[4]) ===
          '%PDF-');

    if (isLikelyPdf) {
      this.getDataDocxHtml = '';
      this.getDataPdfUrl = URL.createObjectURL(
        new Blob([new Uint8Array(data)], { type: 'application/pdf' }),
      );
      return;
    }

    if (low.endsWith('.docx')) {
      this.getDataPdfUrl = null;
      this.getDataDocxHtml = '';
      const blob = new Blob([new Uint8Array(data)], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      this.setGetDataDownloadFromBlob(blob, base);
      return;
    }

    if (low.endsWith('.doc') && !low.endsWith('.docx')) {
      this.getDataPdfUrl = null;
      this.getDataDocxHtml = '';
      const blob = new Blob([new Uint8Array(data)], {
        type: 'application/msword',
      });
      const ab = await blob.arrayBuffer();
      const pdfBlob = await this.convertLegacyDocBufferToPdfBlob(ab);
      if (pdfBlob) {
        this.getDataPdfUrl = URL.createObjectURL(pdfBlob);
        // For non-PDF files, always offer download even if we can preview (DOCâ†’PDF).
        this.setGetDataDownloadFromBlob(blob, base);
        return;
      }
      this.setGetDataDownloadFromBlob(blob, base);
      return;
    }

    // Unsupported file inside ZIP: offer download instead of showing a blank panel.
    this.getDataPdfUrl = null;
    this.getDataDocxHtml = '';
    this.setGetDataDownloadFromBlob(new Blob([new Uint8Array(data)]), base);
  }

  private async extractLegacyDocPdfFromZipBlob(
    zipBlob: Blob,
  ): Promise<Blob | null> {
    const raw = new Uint8Array(await zipBlob.arrayBuffer());
    let files: Record<string, Uint8Array>;
    try {
      files = unzipSync(raw);
    } catch (e) {
      console.warn('Get Data: fflate unzipSync failed (legacy doc extract)', e);
      return null;
    }
    const docKeys = Object.keys(files).filter(
      (p) =>
        p &&
        !p.endsWith('/') &&
        p.toLowerCase().endsWith('.doc') &&
        !p.toLowerCase().endsWith('.docx'),
    );
    if (docKeys.length === 0) return null;
    docKeys.sort();
    const data = files[docKeys[0]];
    if (!data?.byteLength) return null;
    const ab = await new Blob([new Uint8Array(data)], {
      type: 'application/msword',
    }).arrayBuffer();
    return this.convertLegacyDocBufferToPdfBlob(ab);
  }

  /** Shared PDF / DOCX / ZIP handling for Get Data FILE mode once blob bytes are available. */
  private async processGetDataFileBlob(
    effectiveBlob: Blob,
    filename: string,
    headerContentType: string,
  ): Promise<void> {
    if (this.getDataPdfUrl?.startsWith('blob:'))
      URL.revokeObjectURL(this.getDataPdfUrl);
    this.getDataPdfUrl = null;
    this.getDataDocxHtml = '';
    this.revokeGetDataFileDownloadUrl();

    const named = (filename || '').replace(/\\/g, '/');
    this.getDataFileName =
      (named.includes('/') ? named.split('/').pop() : named) || named || '';

    const ct = (effectiveBlob.type || headerContentType || '').toLowerCase();
    const extFromName = (name: string): string => {
      const n = name.toLowerCase();
      const i = n.lastIndexOf('.');
      return i >= 0 ? n.slice(i) : '';
    };
    let ext = extFromName(filename);
    if (!ext) {
      const ff = (this.datasetDetails?.fileFormat ?? '')
        .toString()
        .toLowerCase()
        .replace(/^\./, '');
      if (ff) ext = '.' + ff;
    }
    if (!ext) {
      const mimeMap: Record<string, string> = {
        'application/pdf': '.pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          '.docx',
        'application/msword': '.doc',
        'application/zip': '.zip',
      };
      ext = mimeMap[ct.split(';')[0]?.trim() || ''] || '';
    }

    const isPdf =
      ext === '.pdf' || ct.includes('pdf') || headerContentType.includes('pdf');
    const isDocx =
      ext === '.docx' ||
      ct.includes('wordprocessingml') ||
      headerContentType.includes('wordprocessingml');
    const isDoc = ext === '.doc' || ct.includes('msword');

    if (isPdf) {
      this.getDataDocxHtml = '';
      this.getDataPdfUrl = URL.createObjectURL(effectiveBlob);
      return;
    }

    if (isDocx) {
      this.getDataDocxHtml = '';
      this.setGetDataDownloadFromBlob(
        effectiveBlob,
        this.getDataFileName || filename,
      );
      return;
    }

    if (isDoc) {
      const ab = await effectiveBlob.arrayBuffer();
      const pdfBlob = await this.convertLegacyDocBufferToPdfBlob(ab);
      if (pdfBlob) {
        this.getDataPdfUrl = URL.createObjectURL(pdfBlob);
        // For non-PDF files, always offer download even if we can preview (DOCâ†’PDF).
        this.setGetDataDownloadFromBlob(
          effectiveBlob,
          this.getDataFileName || filename,
        );
        return;
      }
      this.setGetDataDownloadFromBlob(effectiveBlob);
      return;
    }

    const isZip =
      ext === '.zip' ||
      /\bapplication\/(zip|x-zip-compressed)\b/i.test(ct) ||
      (await this.blobStartsWithZipMagic(effectiveBlob));
    if (isZip) {
      const raw = new Uint8Array(await effectiveBlob.arrayBuffer());
      let unzipped: Record<string, Uint8Array>;
      try {
        unzipped = unzipSync(raw);
      } catch (e) {
        console.warn('Get Data: fflate unzipSync failed', e);
        this.setGetDataDownloadFromBlob(effectiveBlob);
        return;
      }
      const previewPaths = this.sortedZipPreviewPaths(unzipped);
      if (previewPaths.length > 1) {
        this.getDataZipContents = unzipped;
        this.getDataFileKeys = previewPaths;
        this.getDataSelectedFileKey = previewPaths[0];
        await this.renderGetDataZipEntry(unzipped, previewPaths[0]);
        return;
      }
      if (previewPaths.length === 1) {
        await this.renderGetDataZipEntry(unzipped, previewPaths[0]);
        return;
      }

      const pdfFromZip = await this.extractPdfFromZipBlob(effectiveBlob);
      if (pdfFromZip) {
        this.getDataPdfUrl = URL.createObjectURL(pdfFromZip);
        return;
      }
      const docxFromZip = await this.extractDocxFromZipBlob(effectiveBlob);
      if (docxFromZip) {
        this.getDataDocxHtml = '';
        this.setGetDataDownloadFromBlob(docxFromZip);
        return;
      }
      const docPdfFromZip =
        await this.extractLegacyDocPdfFromZipBlob(effectiveBlob);
      if (docPdfFromZip) {
        this.getDataDocxHtml = '';
        this.getDataPdfUrl = URL.createObjectURL(docPdfFromZip);
        return;
      }
    }

    if (
      !this.getDataPdfUrl &&
      !(this.getDataDocxHtml && this.getDataDocxHtml.trim())
    ) {
      this.setGetDataDownloadFromBlob(effectiveBlob);
    }
  }

  private extractGetDataEntries(response: unknown): any[] {
    if (response == null) return [];
    if (Array.isArray(response)) return response;
    if (typeof response !== 'object') return [];
    const body = response as Record<string, unknown>;
    if (Array.isArray(body['result'])) return body['result'] as any[];
    if (Array.isArray(body['results'])) return body['results'] as any[];
    if (Array.isArray(body['data'])) return body['data'] as any[];
    if ('id' in body || 'type' in body) return [body];
    return [];
  }

  async loadGetData(page: number): Promise<void> {
    if (!this.datasetId) {
      return;
    }

    const targetPage = Math.min(
      Math.max(1, page),
      this.getGetDataMaxViewablePage(),
    );

    this.getDataLoading = true;
    try {
      // NGSI-LD Get Data: dataplane without /rsp. Gateway uses /rsp via app-gateway-data.
      const ngsiBase = (
        environment.ngsiLdApiPath ||
        environment.baseRsPathUrl ||
        ''
      ).replace(/\/+$/, '');
      const ngsiOpts = ngsiBase ? { baseUrl: ngsiBase } : undefined;

      const response = await this.rsApiService.getNgsiLdEntities(
        this.datasetId,
        targetPage,
        ngsiOpts,
      );
      this.getDataResponse = response;

      if (response?.paginationInfo) {
        this.getDataTotalPages = response.paginationInfo.totalPages || 1;
        this.getDataTotalEntries = response.paginationInfo.totalCount || 0;
        this.getDataPageSize = response.paginationInfo.size || 8;
      }

      this.getDataEntries = this.extractGetDataEntries(response).slice(0, 8);
      this.getDataCurrentPage = targetPage;
    } catch (error) {
      console.error('Error loading get data:', error);
      this.toasterService.error('Failed to load data. Please try again.');
      this.getDataEntries = [];
    } finally {
      this.getDataLoading = false;
    }
  }

  onGetDataPageChange(page: number): void {
    const maxPage = this.getGetDataMaxViewablePage();
    const targetPage = Math.min(Math.max(1, page), maxPage);
    if (targetPage !== this.getDataCurrentPage) {
      this.loadGetData(targetPage);
    }
  }

  onGetDataFirstPage(): void {
    if (this.getDataCurrentPage !== 1) {
      this.onGetDataPageChange(1);
    }
  }

  onGetDataLastPage(): void {
    const lastViewablePage = this.getGetDataMaxViewablePage();
    if (this.getDataCurrentPage !== lastViewablePage) {
      this.onGetDataPageChange(lastViewablePage);
    }
  }

  getGetDataMaxViewablePage(): number {
    return Math.min(this.getDataTotalPages, this.getDataMaxViewPages);
  }

  shouldShowGetDataPageLimitAlert(): boolean {
    return this.getDataTotalEntries > this.getDataMaxViewRecords;
  }

  async downloadGetDataCSV(): Promise<void> {
    if (!this.datasetId) {
      this.toasterService.warning('Dataset ID is not available');
      return;
    }

    try {
      // Call the downloadCSVNgslid API endpoint
      const blob = await this.rsApiService.downloadCSVNgslid(this.datasetId);

      // Create download link and trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `dataset-${this.datasetId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Optimistically update download count (downloadCSVNgslid uses baseRsPathUrl/RS)
      this.activityMetrics.downloads =
        (this.activityMetrics.downloads ?? 0) + 1;
      this.cdr.detectChanges();
      this.googleAnalytics.trackDatasetDownload(
        this.headerConfig.title ?? '',
        this.datasetId ?? '',
      );
      this.toasterService.success('CSV file downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      this.toasterService.error('Failed to download CSV file');
    }
  }

  private extractHeaders(entries: any[]): string[] {
    if (!entries || entries.length === 0) return [];

    const headerSet = new Set<string>();
    entries.forEach((entry) => {
      this.flattenObject(entry, '', headerSet);
    });

    return Array.from(headerSet).sort();
  }

  private flattenObject(
    obj: any,
    prefix: string,
    headerSet: Set<string>,
  ): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (
          obj[key] !== null &&
          typeof obj[key] === 'object' &&
          !Array.isArray(obj[key])
        ) {
          this.flattenObject(obj[key], newKey, headerSet);
        } else {
          headerSet.add(newKey);
        }
      }
    }
  }

  private getNestedValue(obj: any, path: string): any {
    if (obj == null) return null;
    if (
      typeof obj === 'object' &&
      Object.prototype.hasOwnProperty.call(obj, path)
    ) {
      return obj[path];
    }
    if (typeof obj !== 'object' || !path.includes('.')) {
      return obj?.[path] ?? null;
    }
    const parts = path.split('.');
    for (let take = parts.length - 1; take >= 1; take--) {
      const head = parts.slice(0, take).join('.');
      if (Object.prototype.hasOwnProperty.call(obj, head)) {
        return this.getNestedValue(obj[head], parts.slice(take).join('.'));
      }
    }
    return null;
  }

  getGetDataStartEntry(): number {
    return (this.getDataCurrentPage - 1) * this.getDataPageSize + 1;
  }

  getGetDataEndEntry(): number {
    const end = this.getDataCurrentPage * this.getDataPageSize;
    return Math.min(end, this.getDataTotalEntries);
  }

  getGetDataPageNumbers(): number[] {
    const pages: number[] = [];
    const maxViewablePages = this.getGetDataMaxViewablePage();
    const maxPages = Math.min(5, maxViewablePages);
    let startPage = Math.max(
      1,
      this.getDataCurrentPage - Math.floor(maxPages / 2),
    );
    const endPage = Math.min(maxViewablePages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  /** Open View File modal: ensure files are loaded, then open modal and load first file. */
  async onViewFile(): Promise<void> {
    if (!this.datasetId) return;
    if (this.shouldDisableRsActionsUntilAccessOk) return;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.googleAnalytics.trackDatasetViewFileClick(
      this.headerConfig.title ?? '',
      this.datasetId ?? '',
    );
    if (this.versionFiles.length === 0) {
      await this.loadDetailsTabData();
    }
    if (this.versionFiles.length === 0) {
      this.toasterService.error('No files available to view.');
      return;
    }
    this.openViewFileModal();
  }

  openViewFileModal(): void {
    this.showViewFileModal = true;
    this.viewFilePdfUrl = null;
    this.viewFileDocxHtml = '';
    if (this.hasViewFilePdf && this.hasViewFileDocx) {
      this.viewFileActiveTab = 'pdf';
    } else if (this.hasViewFilePdf) {
      this.viewFileActiveTab = 'pdf';
    } else {
      this.viewFileActiveTab = 'docx';
    }
    this.loadViewFileContent();
  }

  closeViewFileModal(): void {
    this.showViewFileModal = false;
    if (this.viewFilePdfUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFilePdfUrl);
    }
    this.viewFilePdfUrl = null;
    if (this.viewFileDocxDownloadUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFileDocxDownloadUrl);
    }
    this.viewFileDocxDownloadUrl = null;
    this.viewFileDocxHtml = '';
    this.viewFileDocxFileName = '';
    this.viewFileLoading = false;
  }

  onViewFileModalVisibleChange(visible: boolean): void {
    this.showViewFileModal = visible;
    if (!visible) {
      this.closeViewFileModal();
    }
  }

  onViewFileTabChange(tab: 'pdf' | 'docx'): void {
    this.viewFileActiveTab = tab;
    this.loadViewFileContent();
  }

  async loadViewFileContent(): Promise<void> {
    if (!this.datasetId) return;
    const isPdf = this.viewFileActiveTab === 'pdf';
    const files = isPdf ? this.versionFilesPdf : this.versionFilesDocx;
    const first = files[0];
    if (!first?.name) {
      this.viewFilePdfUrl = null;
      this.viewFileDocxHtml = '';
      this.viewFileLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.viewFileLoading = true;
    if (this.viewFilePdfUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFilePdfUrl);
    }
    this.viewFilePdfUrl = null;
    if (this.viewFileDocxDownloadUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFileDocxDownloadUrl);
    }
    this.viewFileDocxDownloadUrl = null;
    this.viewFileDocxHtml = '';
    this.cdr.markForCheck();
    try {
      if (isPdf) {
        const { presignedUrl } =
          await this.fileApiService.getPresignedDownloadUrl(this.datasetId, {
            key: first.name,
            presigned: true,
          });
        this.viewFilePdfUrl = presignedUrl;
        this.viewFileDocxHtml = '';
      } else {
        // Blob URL avoids the browser opening Office docs inline from cross-origin presigned URLs.
        const blob = await this.fileApiService.getFileBlob(this.datasetId, {
          key: first.name,
          presigned: false,
        });
        const nameLc = (first.name || '').toLowerCase();
        const mime = nameLc.endsWith('.docx')
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : nameLc.endsWith('.doc')
            ? 'application/msword'
            : blob.type || 'application/octet-stream';
        const typedBlob =
          blob.type && blob.type !== 'application/octet-stream'
            ? blob
            : new Blob([await blob.arrayBuffer()], { type: mime });
        this.viewFilePdfUrl = null;
        this.viewFileDocxHtml = '';
        this.viewFileDocxDownloadUrl = URL.createObjectURL(typedBlob);
        this.viewFileDocxFileName = first.name;
      }
    } catch (err) {
      console.error('Error loading file for view:', err);
      this.toasterService.error('Failed to load file for viewing.');
    } finally {
      this.viewFileLoading = false;
      this.cdr.markForCheck();
    }
  }

  get viewFileDocxSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.viewFileDocxHtml || '');
  }

  get viewFileDocxSafeDownloadUrl(): SafeResourceUrl | null {
    return this.viewFileDocxDownloadUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          this.viewFileDocxDownloadUrl,
        )
      : null;
  }

  get viewFilePdfSafeUrl(): SafeResourceUrl | null {
    return this.viewFilePdfUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.viewFilePdfUrl)
      : null;
  }

  get getDataPdfSafeUrl(): SafeResourceUrl | null {
    return this.getDataPdfUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.getDataPdfUrl)
      : null;
  }

  get getDataModalFileHint(): string {
    return this.getDataFileName || '';
  }

  get getDataDocxSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.getDataDocxHtml || '');
  }

  get getDataFileDownloadSafeUrl(): SafeResourceUrl | null {
    return this.getDataFileDownloadUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getDataFileDownloadUrl,
        )
      : null;
  }

  onCardAction(action: string): void {
    if (this.isAssetReadOnly) {
      return;
    }

    console.log('Card action clicked:', action);
    const name = this.headerConfig.title ?? '';
    const id = this.datasetId ?? '';
    if (action === 'sample-data') {
      this.googleAnalytics.trackDatasetSampleDataClick(name, id);
      this.openSampleDataModal();
    } else if (action === 'data-descriptors') {
      this.googleAnalytics.trackDatasetDataDescriptorsClick(name, id);
      this.openDataDescriptorsModal();
    } else if (action === 'view-code') {
      this.googleAnalytics.trackDatasetViewCodeClick(name, id);
      this.openViewCodeModal();
    } else if (action === 'query-data') {
      this.openQueryDataModal();
    } else {
      // TODO: Implement other action handlers
    }
  }

  openDataDescriptorsModal(): void {
    if (this.dataDescriptors.length > 0) {
      this.selectedDataElement = this.dataDescriptors[0].name;
    } else {
      this.selectedDataElement = '';
    }
    this.showDataDescriptorsModal = true;
    this.cdr.detectChanges();
  }

  closeDataDescriptorsModal(): void {
    this.showDataDescriptorsModal = false;
  }

  selectDataElement(elementName: string): void {
    this.selectedDataElement = elementName;
    this.cdr.detectChanges();
  }

  get selectedDescriptor(): CatalogueDataDescriptorViewModel | undefined {
    return (
      this.dataDescriptors.find((d) => d.name === this.selectedDataElement) ||
      this.dataDescriptors[0]
    );
  }

  descriptorSubtitle(descriptor: CatalogueDataDescriptorViewModel): string {
    return catalogueDescriptorSubtitle(descriptor);
  }

  showDescriptorDetailField(
    value: string | number | boolean | null | undefined,
  ): boolean {
    return hasCatalogueDescriptorFieldValue(value);
  }

  /** Sample value for the selected descriptor from item API dataSample (e.g. "Ranjangaon ganpati road" for Address). */
  get selectedDescriptorSampleValue():
    | string
    | number
    | boolean
    | null
    | undefined {
    const desc = this.selectedDescriptor;
    if (!desc?.name) return undefined;
    const sample =
      this.sampleData ??
      this.fullDatasetData?.dataSample ??
      this.fullDatasetData?.data_sample;
    if (!sample || typeof sample !== 'object') return undefined;
    let raw = sample[desc.name];
    if (
      raw === undefined &&
      typeof sample === 'object' &&
      !Array.isArray(sample)
    ) {
      const key = Object.keys(sample).find(
        (k) => k.toLowerCase() === desc.name.toLowerCase(),
      );
      raw = key !== undefined ? sample[key] : undefined;
    }
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw === 'object') return JSON.stringify(raw);
    return raw;
  }

  /**
   * Format any date string (ISO or display format like "10 February, 2026 - 11:57 AM") to DD-MMM-YYYY HH:mm IST.
   * Ensures Updated at in Dataset details shows short month, 24h time, and IST (same as Created at).
   */
  private formatUpdatedAtForCard(dateString: string): string {
    if (!dateString || typeof dateString !== 'string') return '';
    const trimmed = dateString.trim();
    if (!trimmed) return '';
    try {
      const d = new Date(trimmed);
      if (isNaN(d.getTime())) return trimmed;
      const iso = d.toISOString();
      return this.formatService.formatDateInIST(iso) || trimmed;
    } catch {
      return trimmed;
    }
  }

  getCreatedAtDate(createdAt: string): string {
    if (!createdAt) return '';
    const parts = createdAt.split(' ');
    return parts[0] || '';
  }

  getCreatedAtTime(createdAt: string): string | null {
    if (!createdAt) return null;
    const parts = createdAt.split(' ');
    // Include time and timezone (e.g. "03:44 IST") when present
    return parts.length > 1 ? parts.slice(1).join(' ') : null;
  }

  getDatePart(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const parts = dateTimeString.split(' ');
    return parts[0] || '';
  }

  getTimePart(dateTimeString: string): string | null {
    if (!dateTimeString) return null;
    const parts = dateTimeString.split(' ');
    return parts.length > 1 ? parts[1] : null;
  }

  isDateTimeFormat(value: string): boolean {
    if (!value) return false;
    // Check if it contains a space and looks like a date-time format (e.g., "16-Jul-2025 02:10")
    return value.includes(' ') && /\d{2}-\w{3}-\d{4}\s+\d{2}:\d{2}/.test(value);
  }

  openSampleDataModal(): void {
    this.showSampleDataModal = true;
  }

  closeSampleDataModal(): void {
    this.showSampleDataModal = false;
  }

  downloadSampleData(): void {
    const dataStr =
      this.formattedSampleData || JSON.stringify(this.sampleData, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: this.sampleDataDownloadMimeType,
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.sampleDataDownloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  copySampleData(): void {
    const dataStr = this.formattedSampleData;
    if (!dataStr) return;
    navigator.clipboard
      .writeText(dataStr)
      .then(() => {
        this.toasterService.success('Sample data copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        this.toasterService.error('Failed to copy to clipboard');
      });
  }

  get formattedSampleData(): string {
    const d = this.sampleData;
    if (d == null) return '';
    if (typeof d === 'string') {
      try {
        const parsed = JSON.parse(d);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return d;
      }
    }
    return JSON.stringify(d, null, 2);
  }

  /** Sample data is always JSON (or GeoJSON when the payload looks like it). */
  get sampleDataFormatLabel(): string {
    return this.isSampleDataGeoJson ? 'GEOJSON' : 'JSON';
  }

  get sampleDataDownloadFilename(): string {
    return this.isSampleDataGeoJson
      ? 'sample-data.geojson'
      : 'sample-data.json';
  }

  get sampleDataDownloadMimeType(): string {
    return this.isSampleDataGeoJson
      ? 'application/geo+json'
      : 'application/json';
  }

  private get isSampleDataGeoJson(): boolean {
    const d = this.sampleData;
    if (d == null) return false;
    if (typeof d === 'string') {
      try {
        return this.isGeoJsonLike(JSON.parse(d));
      } catch {
        return false;
      }
    }
    if (Array.isArray(d)) {
      return d.some((item) => this.isGeoJsonLike(item));
    }
    return this.isGeoJsonLike(d);
  }

  private isGeoJsonLike(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const obj = value as Record<string, unknown>;
    const type = obj['type'];
    if (type === 'Feature' || type === 'FeatureCollection') return true;
    return obj['geometry'] != null && typeof obj['geometry'] === 'object';
  }

  get sampleDataLineCount(): number {
    const s = this.formattedSampleData;
    if (!s) return 0;
    return s.split('\n').length;
  }

  async openViewCodeModal(): Promise<void> {
    // Initialize tabs based on resourceServer array
    await this.initializeViewCodeTabs();
    this.showViewCodeModal = true;
  }

  async initializeViewCodeTabs(): Promise<void> {
    this.viewCodeTabs = [];
    this.viewCodeByTab = {};

    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer) {
      // No resourceServer info available: show the default NGSI-LD sample.
      this.viewCodeTabs = [{ name: 'Default', resourceServer: null }];
      this.viewCodeActiveTab = 'Default';
      try {
        this.viewCodeByTab['Default'] =
          await this.loadCodeFileForResourceServer('NGSI-LD');
      } catch (error) {
        console.error('Error loading default code file:', error);
        this.viewCodeByTab['Default'] =
          this.generateCodeForResourceServer(null);
      }
      return;
    }

    const resourceServers = Array.isArray(this.fullDatasetData.resourceServer)
      ? this.fullDatasetData.resourceServer
      : [this.fullDatasetData.resourceServer];

    // Load code files for each resourceServer
    for (const rs of resourceServers) {
      const tabName = rs.name || 'Default';
      this.viewCodeTabs.push({ name: tabName, resourceServer: rs });

      // Load the appropriate code file based on resourceServer name
      try {
        const codeContent = await this.loadCodeFileForResourceServer(
          tabName,
          rs,
        );
        this.viewCodeByTab[tabName] = codeContent;
      } catch (error) {
        console.error(`Error loading code file for ${tabName}:`, error);
        // Fallback to generated code
        this.viewCodeByTab[tabName] = this.generateCodeForResourceServer(rs);
      }
    }

    // Set first tab as active
    if (this.viewCodeTabs.length > 0) {
      this.viewCodeActiveTab = this.viewCodeTabs[0].name;
    }
  }

  /** True when NGSI-LD view code should use temporal search (queryTypes includes TEMPORAL and ATTR). */
  private shouldUseTemporalNgsiLdViewCode(resourceServer?: any): boolean {
    const queryTypes = resourceServer?.queryTypes;
    if (Array.isArray(queryTypes)) {
      const upper = queryTypes.map((t: string) => String(t).toUpperCase());
      return upper.includes('TEMPORAL') && upper.includes('ATTR');
    }
    return this.hasQueryType('TEMPORAL') && this.hasQueryType('ATTR');
  }

  private getTemporalFieldName(): string {
    const dtDescriptor = this.dataDescriptors.find(
      (d) =>
        d.parameterType.toLowerCase().includes('datetime') ||
        d.name === 'observationDateTime',
    );
    return dtDescriptor?.name || 'observationDateTime';
  }

  async loadCodeFileForResourceServer(
    resourceServerName: string,
    resourceServer?: any,
  ): Promise<string> {
    // Map resourceServer names to file names
    const fileMap: { [key: string]: string } = {
      'NGSI-LD': 'viewCode-ngsi-ld_get_paginated_data.py',
      OGC: 'viewCode-OGC_Feature_data.py',
      Gateway: 'viewCode-gateway_get-data.py',
      GATEWAY: 'viewCode-gateway_get-data.py',
      FILE: 'viewCode-FILE_data.py',
    };

    const defaultNgsiFile = this.shouldUseTemporalNgsiLdViewCode(resourceServer)
      ? 'viewCode-ngsi-ld_get_temporal_data.py'
      : 'viewCode-ngsi-ld_get_paginated_data.py';
    const fileName =
      resourceServerName === 'NGSI-LD' || !fileMap[resourceServerName]
        ? defaultNgsiFile
        : fileMap[resourceServerName];
    const filePath = `/${fileName}`;

    try {
      let codeContent = await firstValueFrom(
        this.http.get(filePath, { responseType: 'text' }),
      );

      // File Server sample is shown as-is except BASE_URL (current origin) and DATABANK_ID (dataset id).
      if (fileName === 'viewCode-FILE_data.py') {
        const origin = window.location.origin;
        codeContent = codeContent.replace(
          /(^|\n)BASE_URL\s*=\s*"[^"]+"/g,
          `$1BASE_URL = "${origin}"`,
        );
        if (this.datasetId) {
          codeContent = codeContent.replace(
            /DATABANK_ID\s*=\s*"[^"]*"/,
            `DATABANK_ID = "${this.datasetId}"`,
          );
        }
        return codeContent;
      }

      // Replace environment variable with actual control plane URL
      const controlPlaneUrl = `${environment.basePathUrl}${environment.authApiPath}token`;
      // Replace os.getenv("CONTROL_PLANE_URL", "default") patterns
      // Pattern 1: CONTROL_PLANE_URL = os.getenv("CONTROL_PLANE_URL", "...")
      codeContent = codeContent.replace(
        /CONTROL_PLANE_URL\s*=\s*os\.getenv\s*\(\s*["']CONTROL_PLANE_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `CONTROL_PLANE_URL = "${controlPlaneUrl}"`,
      );
      // Pattern 2: control_plane_url = os.getenv("CONTROL_PLANE_URL", "...")
      codeContent = codeContent.replace(
        /control_plane_url\s*=\s*os\.getenv\s*\(\s*["']CONTROL_PLANE_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `control_plane_url = "${controlPlaneUrl}"`,
      );

      // OGC Features-style items URL: base from environment + collection id (dataset) + example bbox query
      const ogcBase = environment.api_ogc_server_domain
        .replace(/\/+$/, '')
        .replace(/^http:\/\//i, 'https://');
      const ogcCollectionId =
        this.datasetId || '8bad4a9a-0572-4ae1-98d8-ebc04e0f0e6b';
      const ogcItemsQuery =
        'bbox=72.6000,15.6167,80.9000,22.1000&bbox-crs=http://www.opengis.net/def/crs/OGC/1.3/CRS84';
      const ogcApiUrl = `${ogcBase}/collections/${ogcCollectionId}/items?${ogcItemsQuery}`;
      codeContent = codeContent.replace(
        /ogc_api_url\s*=\s*os\.getenv\s*\(\s*["']OGC_API_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `ogc_api_url = "${ogcApiUrl}"`,
      );

      // Replace BASE_RS_PATH_URL environment variable with actual base RS path URL
      const baseRsPathUrl = environment.baseRsPathUrl;
      codeContent = codeContent.replace(
        /BASE_RS_PATH_URL\s*=\s*os\.getenv\s*\(\s*["']BASE_RS_PATH_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `BASE_RS_PATH_URL = "${baseRsPathUrl}"`,
      );

      // Replace BASE_FILE_PATH_URL environment variable with actual base file path URL
      const baseFilePathUrl = environment.baseFilePathUrl;
      codeContent = codeContent.replace(
        /BASE_FILE_PATH_URL\s*=\s*os\.getenv\s*\(\s*["']BASE_FILE_PATH_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `BASE_FILE_PATH_URL = "${baseFilePathUrl}"`,
      );

      // Replace dataset ID in the code if datasetId is available
      if (this.datasetId) {
        // Replace common patterns for dataset/entity IDs in the code
        // Pattern 1: UUIDs in URLs (e.g., /entities/76cd9fb0-4ea7-4938-b4cc-bf541f4a3efa)
        codeContent = codeContent.replace(
          /\/entities\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
          `/entities/${this.datasetId}`,
        );
        // Pattern 2: UUIDs in collections (e.g., /collections/dd60aa4f-24a3-435d-936d-2b2b7f5607a8)
        codeContent = codeContent.replace(
          /\/collections\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
          `/collections/${this.datasetId}`,
        );
        // Pattern 3: entity_id / ENTITY_ID = "..." assignments
        codeContent = codeContent.replace(
          /entity_id\s*=\s*"[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}"/gi,
          `entity_id = "${this.datasetId}"`,
        );
        // Pattern 3b: ENTITY_ID = "..." assignments (temporal search script)
        codeContent = codeContent.replace(
          /ENTITY_ID\s*=\s*"[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}"/gi,
          `ENTITY_ID = "${this.datasetId}"`,
        );
        // Pattern 4: Default ID in else clause
        codeContent = codeContent.replace(
          /entity_id\s*=\s*"[^"]*"\s*#\s*Default ID/gi,
          `entity_id = "${this.datasetId}"  # Default ID`,
        );
        // Pattern 5: FILE server URLs. Template is f"{BASE_FILE_PATH_URL}v1/databanks/..." — no slash before v1
        codeContent = codeContent.replace(
          /v1\/databanks\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\/files/gi,
          `v1/databanks/${this.datasetId}/files`,
        );
      }

      if (fileName === 'viewCode-ngsi-ld_get_temporal_data.py') {
        const timeProperty = this.getTemporalFieldName();
        codeContent = codeContent.replace(
          /TIME_PROPERTY\s*=\s*"[^"]*"/,
          `TIME_PROPERTY = "${timeProperty}"`,
        );
      }

      return codeContent;
    } catch (error) {
      console.error(`Failed to load code file ${fileName}:`, error);
      throw error;
    }
  }

  /**
   * Last-resort fallback used only when loadCodeFileForResourceServer() fails
   * (e.g. the static viewCode-*.py sample couldn't be fetched). Deliberately
   * does not duplicate the real scripts here — keeping a second copy in sync
   * with public/viewCode-*.py is what caused them to drift apart before.
   */
  generateCodeForResourceServer(resourceServer: any): string {
    const rsName = resourceServer?.name || 'this resource server';
    return `# Unable to load the sample code for ${rsName}.\n# Please refresh the page and try again.`;
  }

  setViewCodeTab(tabName: string): void {
    this.viewCodeActiveTab = tabName;
  }

  getCurrentViewCode(): string {
    return this.viewCodeByTab[this.viewCodeActiveTab] || this.viewCode;
  }

  closeViewCodeModal(): void {
    this.showViewCodeModal = false;
  }

  downloadCode(): void {
    const code = this.getCurrentViewCode();
    const codeBlob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(codeBlob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = this.viewCodeActiveTab
      ? `code_${this.viewCodeActiveTab.toLowerCase().replace(/\s+/g, '_')}.py`
      : 'code.py';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  copyCode(): void {
    const code = this.getCurrentViewCode();
    navigator.clipboard
      .writeText(code)
      .then(() => {
        this.toasterService.success('Code copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        this.toasterService.error('Failed to copy code');
      });
  }

  async openQueryDataModal(): Promise<void> {
    if (this.shouldDisableRsActionsUntilAccessOk) return;
    // Check if user is logged in
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();

      if (!isLoggedIn) {
        // Show login modal if not logged in
        this.showLoginModal = true;
        return;
      }

      // User is logged in, open query data modal (date range filter)
      this.showQueryDataModal = true;
      this.queryDataDateRange = { startDate: null, endDate: null };
    } catch (error) {
      console.error('Error checking login status:', error);
      // If there's an error checking login, show login modal as fallback
      this.showLoginModal = true;
    }
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      // Call getUserInfo API
      const userData = await this.authApiService.getUserInfo();
      this.setCurrentUserSubFromUserData(userData);
      this.hasLoadedCurrentUserInfo = true;
      console.log('User data response:', userData);

      if (userData && userData['result']) {
        const result = userData['result'];
        console.log('User result:', result);
        console.log('Organisation object:', result['organisation']);

        // Track organisation status so we can avoid pre-populating when pending
        let organisationStatus: string | null = null;

        // First, check if organisationName or name is directly available in user data
        let organisationName: string | null = null;
        if (result['organisation']) {
          const org = result['organisation'];
          // Read status and normalise
          const rawStatus = org['status'] ?? null;
          organisationStatus = rawStatus
            ? String(rawStatus).toLowerCase().trim()
            : null;

          // If status is pending, do NOT pre-populate organisation name
          if (organisationStatus !== 'pending') {
            // Check for organisationName first, then name as fallback
            organisationName = org['organisationName'] || org['name'] || null;
            console.log(
              'Checking organisation fields - organisationName:',
              org['organisationName'],
              'name:',
              org['name'],
              'status:',
              organisationStatus,
            );
            if (organisationName) {
              console.log(
                'Organisation name found directly in user data:',
                organisationName,
              );
            } else {
              console.log('No organisation name found in organisation object');
            }
          } else {
            console.log(
              'Organisation status is pending; will not pre-populate organisation name.',
            );
          }
        } else {
          console.log('No organisation object found in user result');
        }

        // If organisationName is not directly available and status is not pending,
        // try to get it via organisationId
        if (!organisationName && organisationStatus !== 'pending') {
          let organisationId: string | null = null;
          if (
            result['organisation'] &&
            result['organisation']['organisationId']
          ) {
            organisationId = result['organisation']['organisationId'];
          }
          console.log('Organisation ID from user:', organisationId);

          if (organisationId) {
            // Call getAllOrganisations API
            const organisationsResponse =
              await this.authApiService.getAllOrganisations(1, 100);
            console.log('Organisations response:', organisationsResponse);

            // Extract organisations list from response
            let organisations: any[] = [];
            // Handle direct array response (as shown in network tab)
            if (Array.isArray(organisationsResponse)) {
              organisations = organisationsResponse;
            } else if (
              organisationsResponse &&
              organisationsResponse['result']
            ) {
              organisations = Array.isArray(organisationsResponse['result'])
                ? organisationsResponse['result']
                : organisationsResponse['result']['organisations'] || [];
            }
            console.log('Organisations list:', organisations);
            console.log('Organisations count:', organisations.length);

            // Match organisationId with list and get the name
            const matchedOrganisation = organisations.find((org: any) => {
              const orgId = org.id || org.organisationId || org.organisation_id;
              console.log(
                'Comparing orgId:',
                orgId,
                'with organisationId:',
                organisationId,
              );
              return orgId === organisationId;
            });

            console.log('Matched organisation:', matchedOrganisation);

            if (matchedOrganisation) {
              // Prioritize orgName as it's the field name in the API response
              organisationName =
                matchedOrganisation.orgName ||
                matchedOrganisation.name ||
                matchedOrganisation.organisationName;
              console.log(
                'Organisation name found from organisations list:',
                organisationName,
              );
            } else {
              console.warn(
                'No matching organisation found for ID:',
                organisationId,
              );
            }
          } else {
            console.warn('No organisation ID found in user data');
          }
        }

        // If we have organisationName AND status is not pending, populate the field and
        // disable it; user is treated as having an approved organisation.
        if (organisationName && organisationStatus !== 'pending') {
          this.queryDataForm.organisationName = organisationName;
          this.queryDataForm.requestAs = 'Organisation';
          this.isOrganisationNameDisabled = true;
          this.userHasOrganisation = true;
          console.log(
            'Set organisation name in form and disabled field:',
            this.queryDataForm.organisationName,
          );
        } else {
          this.isOrganisationNameDisabled = false;
          this.userHasOrganisation = false;
          console.log(
            'No organisation name found, show Individual/Organisation choice',
          );
        }
      }
    } catch (error) {
      console.error('Error loading user organisation:', error);
      this.isOrganisationNameDisabled = false;
      this.userHasOrganisation = false;
    }
  }

  private async loadCurrentUserInfo(force = false): Promise<void> {
    if (!this.isLoggedIn) {
      this.currentUserSub = null;
      this.currentUserEmail = null;
      this.hasLoadedCurrentUserInfo = false;
      return;
    }

    if (this.hasLoadedCurrentUserInfo && !force) {
      return;
    }

    try {
      const userData = await this.authApiService.getUserInfo();
      this.setCurrentUserSubFromUserData(userData);
      await this.rolesApiService.getRoles();
    } catch (error) {
      console.error('Error loading current user info:', error);
      this.currentUserSub = this.getCurrentUserSubFromKeycloak();
      this.currentUserEmail = this.getCurrentUserEmailFromKeycloak();
    } finally {
      this.hasLoadedCurrentUserInfo = true;
    }
  }

  private setCurrentUserSubFromUserData(userData: any): void {
    const result = userData?.['result'] ?? userData;
    const sub = result?.['sub'] ?? this.getCurrentUserSubFromKeycloak();
    this.currentUserSub = typeof sub === 'string' && sub.trim() ? sub.trim() : null;
    const email = result?.['email'] ?? result?.['userEmail'] ?? this.getCurrentUserEmailFromKeycloak();
    this.currentUserEmail = typeof email === 'string' && email.trim() ? email.trim() : null;
  }

  private getCurrentUserEmailFromKeycloak(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance() as {
        tokenParsed?: { email?: string; preferred_username?: string };
      };
      const email = instance?.tokenParsed?.email ?? instance?.tokenParsed?.preferred_username;
      return typeof email === 'string' && email.includes('@') ? email.trim() : null;
    } catch {
      return null;
    }
  }

  private getCurrentUserSubFromKeycloak(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance() as any;
      const parsedSub = instance?.tokenParsed?.sub;
      const subject = instance?.subject;
      const sub = parsedSub ?? subject ?? null;
      return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
    } catch {
      return null;
    }
  }

  private refreshApiExplorerContext(): void {
    if (!this.datasetId) {
      this.apiExplorerContext = null;
      return;
    }
    this.apiExplorerContext = {
      datasetId: this.datasetId,
      resourceServers: this.resourceServersList,
      resourceType:
        this.fullDatasetData?.resourceType ??
        this.datasetDetails?.resourceType ??
        this.fullDatasetData?.resource_type,
      iudxResourceAPIs: this.fullDatasetData?.iudxResourceAPIs,
      fileKeys: this.getDataFileKeys,
    };
  }

  openApiExplorer(): void {
    this.refreshApiExplorerContext();
    this.showApiExplorer = true;
    this.cdr.markForCheck();
  }

  onApiExplorerVisibleChange(visible: boolean): void {
    this.showApiExplorer = visible;
  }

  onApiExplorerLoginRequired(): void {
    this.showApiExplorer = false;
    setTimeout(() => {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
  }

  closeQueryDataModal(): void {
    this.showQueryDataModal = false;
    this.queryDataResponse = null;
  }

  /** Called when Query Data modal visibility changes (e.g. user clicks X). Aborts the API call if one is in progress. */
  onQueryDataModalVisibleChange(visible: boolean): void {
    this.showQueryDataModal = visible;
    if (!visible) {
      // If modal is being closed, abort any in-flight request and reset state
      if (this.queryDataLoading) {
        this.queryDataAbortController?.abort();
        this.queryDataLoading = false;
      }
      this.queryDataResponse = null;
    }
  }

  /** Format Date to YYYY-MM-DD for API. */
  private formatDateForApi(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Query Data modal: apply date range filter (Search) â€“ calls NGSI-LD v2 entities search API. */
  async onQueryDataSearch(): Promise<void> {
    if (!this.isQueryDataFormValid) {
      if (this.queryDataDateError) {
        this.toasterService.error(this.queryDataDateError);
      } else {
        this.toasterService.error('Please select a date range.');
      }
      return;
    }
    const from = this.queryDataDateRange?.startDate
      ? this.formatDateForApi(this.queryDataDateRange.startDate)
      : '';
    const to = this.queryDataDateRange?.endDate
      ? this.formatDateForApi(this.queryDataDateRange.endDate)
      : '';
    if (!this.datasetId) {
      this.toasterService.error('Dataset ID is not available.');
      return;
    }
    const timeFrom = `${from}T00:00:00+05:30`;
    const timeTo = `${to}T23:59:59+05:30`;
    const payload = {
      searchCriteria: [
        {
          searchType: 'betweenTemporal',
          field: 'observationDateTime',
          values: [timeFrom, timeTo],
        },
      ],
    };
    this.queryDataLoading = true;
    this.queryDataAbortController = new AbortController();
    try {
      const response = await this.catalogueApiService.searchEntities(
        this.datasetId,
        payload,
        environment.baseRsPathUrl,
        { page: 1, size: 10 },
      );
      this.queryDataResponse = response;
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        return;
      }
      console.error('Entities search API error:', err);
      this.toasterService.error(
        err?.error?.detail ??
          err?.message ??
          'Failed to load temporal data. Please try again.',
      );
    } finally {
      this.queryDataLoading = false;
      this.queryDataAbortController = null;
    }
  }

  /** Query Data modal: clear date range (Reset). */
  onQueryDataReset(): void {
    this.queryDataDateRange = { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  /** Query Data modal: update date range and clear response when user changes selection. */
  onQueryDataDateRangeChange(value: DateRange | null): void {
    this.queryDataDateRange = value ?? { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  /** True when both start and end dates are set and end >= start. */
  get isQueryDataFormValid(): boolean {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return false;
    return to >= from;
  }

  /** Validation error message for date range, or null if valid. */
  get queryDataDateError(): string | null {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return null;
    if (to < from) return 'End date must be on or after start date.';
    return null;
  }

  /** Pretty-printed JSON string for display in Query Data modal. */
  getQueryDataResponseFormatted(): string {
    if (!this.queryDataResponse) return '';
    return JSON.stringify(this.queryDataResponse, null, 2);
  }

  /** Download query data response as JSON file. */
  downloadQueryDataJson(): void {
    if (!this.queryDataResponse) return;
    const jsonStr = JSON.stringify(this.queryDataResponse, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const from = this.queryDataDateRange?.startDate
      ? this.formatDateForApi(this.queryDataDateRange.startDate)
      : 'start';
    const to = this.queryDataDateRange?.endDate
      ? this.formatDateForApi(this.queryDataDateRange.endDate)
      : 'end';
    link.download = `query-data_${from}_to_${to}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  get purposeDescriptionLength(): number {
    return this.queryDataForm.purposeDescription.length;
  }

  get isPurposeDescriptionValid(): boolean {
    return this.purposeDescriptionLength <= this.PURPOSE_MAX_LENGTH;
  }

  get isFormValid(): boolean {
    const organisationNameValid =
      this.queryDataForm.requestAs === 'Individual' ||
      (this.queryDataForm.requestAs === 'Organisation' &&
        this.queryDataForm.organisationName.trim().length > 0);
    return (
      organisationNameValid &&
      this.queryDataForm.purposeDescription.trim().length > 0 &&
      this.isPurposeDescriptionValid &&
      this.queryDataForm.agreeToTerms
    );
  }

  async submitQueryDataRequest(): Promise<void> {
    if (!this.isFormValid || !this.datasetId) {
      return;
    }

    try {
      const payload = {
        itemId: this.datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: this.queryDataForm.requestAs,
          purposeOfRequest: this.queryDataForm.purposeDescription,
          organisationName:
            this.queryDataForm.requestAs === 'Organisation'
              ? this.queryDataForm.organisationName
              : '',
        },
      };

      await this.aclApdApiService.createAccessRequest(payload);
      this.toasterService.success('Your request is sent, Successfully');
      this.closeQueryDataModal();

      this.markAccessRequestPending();
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      await this.checkUserAccess();
    } catch (error) {
      console.error('Error creating access request:', error);
      // Error handling - you might want to show a toast notification here
    }
  }

  openTagsModal(): void {
    this.showTagsModal = true;
  }

  closeTagsModal(): void {
    this.showTagsModal = false;
  }

  get accessType(): string {
    // Get access type from badge (Public, Restricted, etc.)
    const badge =
      this.datasetCardDetails.badge ||
      this.datasetDetails.datasetPermission ||
      '';
    return badge.toLowerCase();
  }

  get isOpenAccess(): boolean {
    // Check if access type is open/public or if accessPolicy is OPEN
    const access = this.accessType;
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toUpperCase();
    return access === 'open' || access === 'public' || accessPolicy === 'OPEN';
  }

  get showDelegateRequestWarning(): boolean {
    return (
      this.workspaceContext.isDelegateSession() &&
      this.isRestrictedAccess &&
      !this.isDatasetOwner &&
      !this.isActiveDelegatorAssetOwner()
    );
  }

  /** Asset owned by the user this delegate is acting for. */
  private isActiveDelegatorAssetOwner(): boolean {
    const ownerUserId = (
      this.fullDatasetData?.ownerUserId ??
      this.fullDatasetData?.owner_user_id ??
      this.fullDatasetData?.ownerUserID ??
      this.datasetDetails?.['ownerUserId'] ??
      ''
    )
      .toString()
      .trim();
    const delegatorId = (this.workspaceContext.getActiveDelegatorId() || '').trim();
    return !!ownerUserId && !!delegatorId && ownerUserId === delegatorId;
  }

  /** Platform admins and delegates do not submit ratings on the catalogue. */
  get canPerformUserFeedback(): boolean {
    return (
      !this.rolesApiService.hasRole(Role.ADMIN) &&
      !this.workspaceContext.isDelegateSession()
    );
  }

  /** Public datasets, owners, or restricted datasets with has_access 200 OK. */
  get canProvideDatasetRating(): boolean {
    if (this.isOpenAccess || this.isDatasetOwner) {
      return true;
    }
    return this.accessStatus.type === DatasetDetailComponent.ACCESS_SUCCESS_URN;
  }

  /** Restricted dataset when has_access is not 200 OK. */
  get showAccessRequiredRatingState(): boolean {
    return this.isRestrictedAccess && !this.canProvideDatasetRating;
  }

  get activityRatingDisplay(): string {
    const rating = this.activityMetrics.rating;
    if (rating == null) {
      return '-';
    }
    return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
  }

  async onGiveRating(): Promise<void> {
    if (!this.canPerformUserFeedback) {
      return;
    }
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch {
      this.isLoggedIn = false;
    }
    if (!this.isLoggedIn) {
      this.storeRatingEditorIntent();
      this.showLoginModal = true;
      this.cdr.detectChanges();
      return;
    }
    if (this.hasOwnPendingRating) {
      this.openOwnRatingEditor();
      return;
    }
    const ownReview = this.myDatasetReview ?? this.ownSubmittedReview;
    if (ownReview && ownReview.status !== 'rejected') {
      this.openOwnRatingView(ownReview);
      return;
    }
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = false;
    this.resetGiveRatingForm();
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  closeGiveRatingModal(): void {
    this.showGiveRatingModal = false;
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = false;
    this.resetGiveRatingForm();
  }

  onGiveRatingModalVisibleChange(visible: boolean): void {
    this.showGiveRatingModal = visible;
    if (!visible) {
      this.isViewingOwnRating = false;
      this.isEditingOwnRating = false;
      this.resetGiveRatingForm();
    }
  }

  selectRating(value: number): void {
    if (this.isViewingOwnRating) {
      return;
    }
    this.selectedRating = value;
  }

  private openOwnRatingView(review: DatasetReviewItem): void {
    this.isViewingOwnRating = true;
    this.isEditingOwnRating = false;
    this.selectedRating = review.ratingValue;
    this.hoveredRating = 0;
    this.ratingFeedback = review.comment;
    this.ratingFormSubmitted = false;
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  private openOwnRatingEditor(): void {
    const review = this.ownSubmittedReview ?? this.myDatasetReview;
    if (!review || !this.hasOwnPendingRating) {
      if (review) {
        this.openOwnRatingView(review);
      }
      return;
    }
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = true;
    this.selectedRating = review.ratingValue;
    this.hoveredRating = 0;
    this.ratingFeedback = review.comment;
    this.editBaselineRating = review.ratingValue;
    this.editBaselineFeedback = review.comment ?? '';
    this.ratingFormSubmitted = false;
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  private storeRatingEditorIntent(): void {
    if (!this.hasOwnPendingRating || !this.datasetId) {
      return;
    }
    try {
      sessionStorage.setItem(DatasetDetailComponent.RATING_EDITOR_INTENT_KEY, this.datasetId);
    } catch {
      /* private mode */
    }
  }

  private openPendingRatingEditorAfterLogin(): void {
    if (!this.canPerformUserFeedback || !this.datasetId || !this.hasOwnPendingRating) {
      return;
    }
    try {
      const intended = sessionStorage.getItem(DatasetDetailComponent.RATING_EDITOR_INTENT_KEY);
      if (intended !== this.datasetId) {
        return;
      }
      sessionStorage.removeItem(DatasetDetailComponent.RATING_EDITOR_INTENT_KEY);
    } catch {
      return;
    }
    this.openOwnRatingEditor();
  }

  get displayedRating(): number {
    return this.hoveredRating || this.selectedRating;
  }

  get ratingFeedbackLength(): number {
    return this.ratingFeedback.length;
  }

  get showRatingRequiredError(): boolean {
    return this.ratingFormSubmitted && this.selectedRating < 1;
  }

  async submitGiveRating(): Promise<void> {
    if (!this.canPerformUserFeedback) {
      return;
    }
    this.ratingFormSubmitted = true;
    if (this.selectedRating < 1) {
      this.toasterService.error('Please select a rating');
      return;
    }
    if (!this.datasetId) {
      this.toasterService.error('Unable to submit rating: dataset id is missing');
      return;
    }
    if (this.isSubmittingRating || (this.blockNewRating && !this.isEditingOwnRating)) {
      return;
    }
    if (this.isEditingOwnRating && !this.hasOwnPendingRating) {
      return;
    }
    if (this.isEditingOwnRating && !this.canSaveRatingEdit) {
      return;
    }

    this.isSubmittingRating = true;
    this.cdr.detectChanges();
    try {
      const comment = this.ratingFeedback.trim().slice(0, 200);
      if (this.isEditingOwnRating) {
        const payload: {
          assetId: string;
          assetType: string;
          entityRating: number;
          actionSubtype?: string;
          actionSubdata?: Record<string, unknown>;
        } = {
          assetId: this.datasetId,
          assetType: 'DATABANK',
          entityRating: this.selectedRating,
        };
        if (comment) {
          payload.actionSubtype = 'feedback';
          payload.actionSubdata = { comment };
        }
        await this.userInteractionApiService.updateUserFeedback(payload);
        this.toasterService.success('Feedback updated.');
      } else {
        await this.userInteractionApiService.postUserFeedback({
          assetId: this.datasetId,
          assetType: 'DATABANK',
          entityRating: this.selectedRating,
          actionSubtype: '',
          actionSubdata: {
            comment,
          },
        });
        this.toasterService.success('Rating submitted. It will appear after platform admin approval.');
      }
      this.closeGiveRatingModal();
      await Promise.all([this.refreshActivityMetrics(), this.loadUserFeedback()]);
    } catch (error: unknown) {
      console.error('Error submitting rating:', error);
      const httpError = error as {
        status?: number;
        error?: { detail?: string; title?: string; type?: string };
      };
      const isConflict =
        httpError?.status === 409 ||
        String(httpError?.error?.type ?? '').includes('conflict');
      if (isConflict) {
        this.toasterService.error(
          httpError?.error?.detail?.trim() || 'Feedback already exists for this asset.',
        );
      }
    } finally {
      this.isSubmittingRating = false;
      this.cdr.detectChanges();
    }
  }

  get canSaveRatingEdit(): boolean {
    return (
      this.selectedRating !== this.editBaselineRating ||
      this.ratingFeedback.trim() !== this.editBaselineFeedback.trim()
    );
  }

  private resetGiveRatingForm(): void {
    this.selectedRating = 0;
    this.hoveredRating = 0;
    this.ratingFeedback = '';
    this.editBaselineRating = 0;
    this.editBaselineFeedback = '';
    this.ratingFormSubmitted = false;
  }

  private readAverageRating(source: Record<string, unknown> | null | undefined): number | null {
    if (!source) {
      return null;
    }
    const raw =
      source['averageRating'] ??
      source['average_rating'] ??
      source['rating'] ??
      source['ratings'];
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) {
      return null;
    }
    return num;
  }

  get isRestrictedAccess(): boolean {
    // Check if access type is restricted
    // Can be determined from badge/permission or accessPolicy field
    const badgeOrPermission = this.accessType === 'restricted';
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toLowerCase();
    const isRestrictedFromPolicy =
      accessPolicy === 'restricted' ||
      accessPolicy === 'secure' ||
      accessPolicy === 'private';

    return badgeOrPermission || isRestrictedFromPolicy;
  }

  get canShowQueryData(): boolean {
    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer)
      return false;
    return this.hasQueryType('TEMPORAL');
  }

  async onRequestAccess(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (this.isRequestAccessButtonDisabled) {
      return;
    }
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
      this.showRequestAccessModal = true;
      await this.loadUserOrganisation();
    } catch {
      this.showLoginModal = true;
    }
  }

  closeRequestAccessModal(): void {
    this.showRequestAccessModal = false;
  }

  goToJoinOrganisation(): void {
    this.closeRequestAccessModal();
    this.router.navigate(['/consumer/join-organisation']);
  }

  async submitRequestAccess(): Promise<void> {
    if (!this.isFormValid || !this.datasetId) return;
    await this._doCreateAccessRequest({
      requestAs: this.queryDataForm.requestAs,
      purposeDescription: this.queryDataForm.purposeDescription,
      organisationName: this.queryDataForm.organisationName,
    });
  }

  /** Handler for the shared RequestAccessComponent's (requestAccessSubmit) event. */
  async onRequestAccessSubmit(formData: {
    requestAs: string;
    purposeDescription: string;
    agreeToTerms: boolean;
    organisationName?: string;
  }): Promise<void> {
    if (!this.datasetId) return;
    await this._doCreateAccessRequest({
      requestAs: formData.requestAs,
      purposeDescription: formData.purposeDescription,
      organisationName: formData.organisationName ?? '',
    });
  }

  private async _doCreateAccessRequest(params: {
    requestAs: string;
    purposeDescription: string;
    organisationName: string;
  }): Promise<void> {
    if (!this.datasetId) return;
    try {
      const payload = {
        itemId: this.datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: params.requestAs,
          purposeOfRequest: params.purposeDescription,
          organisationName:
            params.requestAs === 'Organisation' ? params.organisationName : '',
        },
      };
      await this.aclApdApiService.createAccessRequest(payload);
      this.googleAnalytics.trackRequestAccess(
        this.headerConfig.title ?? '',
        this.datasetId ?? '',
      );
      this.toasterService.success('Your request is sent, Successfully');
      this.closeRequestAccessModal();

      this.markAccessRequestPending();
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      await this.checkUserAccess();
    } catch (error) {
      console.error('Error creating access request:', error);
      this.toasterService.error(
        'Failed to send access request. Please try again.',
      );
    }
  }

  get requestAccessButtonText(): string {
    return this.isAccessRequestPendingState()
      ? 'Request Sent'
      : 'Request Access';
  }

  get isRequestAccessButtonDisabled(): boolean {
    return this.isAccessRequestPendingState();
  }

  /** Remove leading __ open-license marker from API license values. */
  private stripLicenseMarker(license: string): string {
    return license.replace(/^__+/, '').trim();
  }

  /** True when the logged-in user holds the org-admin role. */
  get isOrgAdmin(): boolean {
    return this.rolesApiService.hasRole(Role.ORG_ADMIN);
  }

  /** Org-admin who owns this dataset â€” show Download (provider flow), not Request Access. */
  get isOrgAdminDatasetOwner(): boolean {
    return this.isOrgAdmin && this.isDatasetOwner;
  }

  get shouldShowRequestAccessButton(): boolean {
    if (this.workspaceContext.isDelegateSession()) {
      return false;
    }

    if (this.licenseUsesOpenMarker) {
      return false;
    }

    // Org-admin owners get Download instead (same as provider owner flow).
    if (this.isOrgAdminDatasetOwner) {
      return false;
    }

    // Show button if dataset has restricted access AND accessPolicy is not OPEN
    // BUT hide if user already has access (has_access returned 200 OK)
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toUpperCase();
    const hasAccess =
      this.accessStatus.type === DatasetDetailComponent.ACCESS_SUCCESS_URN;

    // Don't show Request Access button if user already has access or owns the dataset
    if (hasAccess || this.isDatasetOwner) {
      return false;
    }

    return this.isRestrictedAccess && accessPolicy !== 'OPEN';
  }

  get shouldShowDownloadButtonForRestricted(): boolean {
    return false;
  }

  get shouldShowDownloadButtonForPublic(): boolean {
    return false;
  }

  /** True when accessPolicy is Restricted and has_access returned 403 with no-access. Disable Get Data, Query Data, View File. */
  get hasNoAccessToRsOrFile(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = this.accessStatus.type || '';
    return accessType === 'urn:dx:apdServerPanel:no-access';
  }

  /** True until has_access returns 200 OK (restricted datasets). Disable Get Data, View File, Query Data until then. */
  get shouldDisableRsActionsUntilAccessOk(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = this.accessStatus.type || '';
    return accessType !== 'urn:dx:apdServerPanel:success';
  }

  /**
   * FILE databank Get Data: only when open/public, owner, or has_access success â€” same idea as Download on restricted assets.
   * Other Get Data flows (ATTR table, OGC) are unchanged.
   */
  get shouldDisableGetDataForFileServerFlow(): boolean {
    if (!this.isFileServerDatabankGetDataFlow) return false;
    if (this.isDatasetOwner) return false;
    if (this.isOpenAccess) return false;
    return this.accessStatus.type !== 'urn:dx:apdServerPanel:success';
  }

  get isGetDataButtonDisabled(): boolean {
    return (
      this.shouldDisableRsActionsUntilAccessOk ||
      this.shouldDisableGetDataForFileServerFlow
    );
  }

  get getDataButtonTooltip(): string | null {
    if (this.isGetDataButtonDisabled) return 'No Access yet';
    return null;
  }

  /** True when user has no access (no-access or no policy). Disable Details, Visualisation, SQL Playground tabs and show "No Access yet" tooltip. */
  get hasNoAccessForDetailsTabs(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = (this.accessStatus.type || '').toString().trim();
    if (accessType === 'urn:dx:apdServerPanel:no-access') return true;
    if (accessType === 'no policy' || accessType.toLowerCase() === 'no-policy')
      return true;
    return false;
  }

  get isDatasetOwner(): boolean {
    const ownerUserId = (
      this.fullDatasetData?.ownerUserId ??
      this.fullDatasetData?.owner_user_id ??
      this.fullDatasetData?.ownerUserID ??
      this.datasetDetails?.['ownerUserId'] ??
      ''
    )
      .toString()
      .trim();
    const currentSub =
      this.currentUserSub ?? this.getCurrentUserSubFromKeycloak();
    return !!ownerUserId && !!currentSub && ownerUserId === currentSub;
  }

  get isPublishStatusPending(): boolean {
    return (
      this.helperService.isPublishStatusPending(this.fullDatasetData) ||
      this.helperService.isPublishStatusPending(this.datasetDetails)
    );
  }

  get isPublishStatusDeclined(): boolean {
    return (
      this.helperService.isPublishStatusRejected(this.fullDatasetData) ||
      this.helperService.isPublishStatusRejected(this.datasetDetails)
    );
  }

  /** True when itemStatus is DECLINED / REJECTED (Organisation Admin rejection signal). */
  get isItemStatusDeclined(): boolean {
    return (
      this.helperService.isItemStatusRejected(this.fullDatasetData) ||
      this.helperService.isItemStatusRejected(this.datasetDetails)
    );
  }

  /**
   * True when itemStatus is UNVERIFIED / UNVERIFIED-SFTP / UNVERIFIED-API —
   * treat as unapproved by Organisation Admin (SFTP/API and standard flows).
   */
  get isItemStatusUnverified(): boolean {
    return (
      this.helperService.isItemStatusUnverified(this.fullDatasetData) ||
      this.helperService.isItemStatusUnverified(this.datasetDetails)
    );
  }

  /** Org Admin has not approved yet (UNVERIFIED / UNVERIFIED-SFTP / UNVERIFIED-API). */
  get isOrgAdminApprovalPending(): boolean {
    return this.isItemStatusUnverified;
  }

  /** Platform Admin has not approved yet (publishStatus pending / under review). */
  get isPlatformAdminApprovalPending(): boolean {
    return this.isPublishStatusPending;
  }

  /**
   * Role name(s) still pending approval:
   * - only org → Organisation administrator
   * - only platform → Platform Administrator
   * - both → Organisation administrator and Platform Administrator
   */
  get pendingApprovalRolesLabel(): string {
    const orgPending = this.isOrgAdminApprovalPending;
    const platformPending = this.isPlatformAdminApprovalPending;
    if (orgPending && platformPending) {
      return 'the Organisation administrator and Platform Administrator';
    }
    if (orgPending) {
      return 'the Organisation administrator';
    }
    if (platformPending) {
      return 'the Platform Administrator';
    }
    return 'the Organisation administrator and Platform Administrator';
  }

  /**
   * Yellow pending banner for the asset owner only (not org/platform admin reviewers
   * or other viewers): publish pending OR org-admin has not verified
   * (UNVERIFIED / UNVERIFIED-SFTP / UNVERIFIED-API).
   */
  get showPendingApprovalBanner(): boolean {
    if (!this.isDatasetOwner || this.rejectionBannerMessage) {
      return false;
    }
    return (
      this.isOrgAdminApprovalPending || this.isPlatformAdminApprovalPending
    );
  }

  /** User Ratings are for Active assets only. Hidden while Pending or Rejected. */
  get showUserRatingsSection(): boolean {
    return this.canShowDatasetActionButtons;
  }

  /**
   * Yellow-bar rejection message:
   * - itemStatus DECLINED → Organisation Admin (metadata/publish-assets reject)
   * - publishStatus DECLINED → Platform Admin (manage platform assets reject)
   */
  get rejectionBannerMessage(): string | null {
    if (this.isItemStatusDeclined) {
      return 'This dataset has been rejected by the Organization Admin.';
    }
    if (this.isPublishStatusDeclined) {
      return 'This dataset has been rejected by the Platform Admin.';
    }
    return null;
  }

  /** True when dataset action buttons (sample data, query, download, etc.) may be shown. */
  get canShowDatasetActionButtons(): boolean {
    const item = this.fullDatasetData ?? this.datasetDetails;
    if (!item) {
      return false;
    }
    if (this.isItemStatusDeclined || this.isItemStatusUnverified) {
      return false;
    }

    return (
      this.helperService.isDataUploadComplete(item) &&
      this.helperService.isPublishStatusApproved(item)
    );
  }

  /** Strict read-only mode until upload is complete and publish status is approved. */
  get isAssetReadOnly(): boolean {
    return !this.canShowDatasetActionButtons;
  }

  /** False when catalogue item marks upload/processing as incomplete. */
  get isDataUploadReady(): boolean {
    return this.helperService.isDataUploadComplete(
      this.fullDatasetData ?? this.datasetDetails,
    );
  }

  async onDownload(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.datasetId) {
      this.toasterService.error('Dataset ID not available');
      return;
    }

    try {
      // Get presigned download URL from API, then use it to trigger download
      const { downloadUrl } = await this.fileApiService.getDatabankDownloadUrl(
        this.datasetId,
      );
      if (!downloadUrl) {
        this.toasterService.error('No download URL received.');
        return;
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${this.datasetId}_databank.zip`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.activityMetrics.downloads =
        (this.activityMetrics.downloads ?? 0) + 1;
      this.googleAnalytics.trackDatasetDownload(
        this.headerConfig.title ?? '',
        this.datasetId ?? '',
      );
      this.toasterService.success('Download started successfully');
    } catch (error: unknown) {
      console.error('Error getting download URL:', error);
      const err = error as { status?: number; error?: { code?: string } };
      if (err?.status === 404 || err?.error?.code === 'RESOURCE_NOT_FOUND') {
        this.toasterService.error('Unable to find the file to download.');
      } else {
        this.toasterService.error('Failed to download file');
      }
    }
  }

  /**
   * Check if dataset has mediaURL (Github repo link)
   */
  get hasMediaURL(): boolean {
    return !!this.fullDatasetData?.mediaURL;
  }

  /**
   * Get the mediaURL value
   */
  get mediaURL(): string | null {
    return this.fullDatasetData?.mediaURL || null;
  }

  /**
   * Handle Github Repo button click - redirect to mediaURL
   */
  onGithubRepoClick(): void {
    if (this.isAssetReadOnly) {
      return;
    }

    const url = this.mediaURL;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No mediaURL found for dataset');
    }
  }

  async loadSimilarDatasets(): Promise<void> {
    // Only search if we have tags
    if (!this.datasetTags || this.datasetTags.length === 0) {
      console.log('No tags available for similar datasets search');
      this.similarDatasets = [];
      return;
    }

    try {
      console.log('Loading similar datasets with tags:', this.datasetTags);

      // Build search criteria payload
      const searchCriteria: SearchCriteria[] = [
        {
          field: 'tags',
          searchType: 'term',
          values: this.datasetTags,
        },
        {
          field: 'type',
          searchType: 'term',
          values: ['adex:DataBank'],
        },
      ];

      console.log(
        'Calling search API with searchCriteria:',
        JSON.stringify(searchCriteria, null, 2),
      );

      // Call search API with searchCriteria (type filter is already in searchCriteria)
      const response = await this.catalogueApiService.searchAllAssets(
        '', // No search query
        [], // Empty assetTypes - type filter is in searchCriteria
        [], // No custom filter
        1, // page
        10, // size - limit to 10 similar datasets
        'name:asc;itemCreatedAt:asc;', // sort
        searchCriteria, // Search criteria with tags and type filter
      );

      console.log('Search API response received:', response);

      // Extract items from response - handle different response structures
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response?.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response?.items && Array.isArray(response.items)) {
        items = response.items;
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (
        response?.data?.result &&
        Array.isArray(response.data.result)
      ) {
        items = response.data.result;
      } else if (response?.data?.items && Array.isArray(response.data.items)) {
        items = response.data.items;
      }

      console.log('Extracted items from response:', items.length);
      if (items.length > 0) {
        console.log('First item sample:', JSON.stringify(items[0], null, 2));
      }

      // Filter out current dataset and map to FeatureCardData (same as collection page)
      this.similarDatasets = items
        .filter((item: any) => {
          const itemId = item.id || item['@id'] || '';
          return itemId && itemId !== this.datasetId;
        })
        .map((item: any) =>
          this.featureCardMapper.mapApiItemToFeatureCard(item),
        );
    } catch (error) {
      console.error('Error loading similar datasets:', error);
      this.similarDatasets = [];
    }
  }

  /**
   * Open Data Readiness PDF in modal viewer
   */
  async openDataReadinessPdf(): Promise<void> {
    if (!this.datasetId) {
      console.warn('Dataset ID is not available');
      return;
    }

    try {
      const pdfUrl = `${environment.dataReadinessS3Url}${this.datasetId}/data_readiness_report.pdf`;
      console.log('Loading PDF from:', pdfUrl);

      // Use HttpClient to fetch the PDF as a blob
      const blob = await firstValueFrom(
        this.http.get(pdfUrl, {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
          },
        }),
      );

      console.log('PDF blob received, size:', blob.size);

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      console.log('Blob URL created:', blobUrl);

      this.dataReadinessPdfUrl = blobUrl;
      this.showDataReadinessPdfModal = true;
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load Data Readiness Report PDF. Please try again.');
    }
  }

  /**
   * Data readiness as a number 0â€“100 for the circular progress (handles comma/decimal and string).
   */
  get dataReadinessPercent(): number {
    const v = this.datasetCardDetails?.dataReadiness;
    if (v == null || v === '' || v === 'NA') return 0;
    const num =
      typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(num) ? Math.min(100, Math.max(0, num)) : 0;
  }

  /**
   * Stroke-dasharray for the readiness circle (circumference â‰ˆ 100 for r=15.915) so the fill matches the percentage.
   */
  get readinessProgressDashArray(): string {
    const circumference = 100;
    const dash = (this.dataReadinessPercent / 100) * circumference;
    return `${dash} ${circumference}`;
  }

  /**
   * Close Data Readiness PDF modal
   */
  closeDataReadinessPdfModal(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.dataReadinessPdfUrl) {
      URL.revokeObjectURL(this.dataReadinessPdfUrl);
    }
    this.showDataReadinessPdfModal = false;
    this.dataReadinessPdfUrl = null;
  }
}
